/**
 * Student Card Enhanced - Comprehensive Implementation
 *
 * Hierarchy: Fast scan layer → Decision layer → Source layer
 *
 * Tabs:
 *  1. Overview   — Fast scan (<10s): who, what needs attention, quick actions
 *  2. Academics  — Deep academic story: subjects, assessments, assignments, exams, reports, support
 *  3. Attendance  — Pattern recognition: heatmap, streaks, exceptions
 *  4. Finance    — Line-item clarity: invoices, payments, plans, receipts
 *  5. Health     — Permissioned data: alerts, records, incidents, consent
 *  6. Transport  — Wialon-style live tracking
 *  7. Docs        — Document vault: searchable, by type/school/term
 *  8. Timeline    — Narrative layer: unified activity feed
 */

import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  User, MapPin, Bus, Wallet, Calendar, BookOpen, AlertTriangle,
  Clock, ChevronRight, ChevronDown, Phone, Mail, Download, MessageCircle,
  CheckCircle, XCircle, AlertCircle, Star, Award, Shield,
  TrendingUp, TrendingDown, CreditCard, Bell, Heart, FileText, Eye, Printer,
  Activity, BarChart3, Trophy, Target, PenTool, ClipboardList, FilePen,
  Users, Building2, GraduationCap, Stethoscope, Flame, Zap, Layers,
  Navigation, Radio, Gauge, Fuel as FuelIcon, Crosshair, ExternalLink, Play, Pause,
  RotateCcw, History, Send, MoreHorizontal, Filter, Search, X, Info,
  Minus, ArrowUp, ArrowDown, ArrowRight, Sparkles, Brain, Settings, BellRing,
  Sun, Moon, CloudSun, Utensils, Coffee, Thermometer, Pill, ShieldCheck,
  Video, Camera, Mic, Lock, CircleDot, Wifi, Snowflake, Users as UsersIcon,
  Route as RouteIcon, TriangleAlert, Disc, Bookmark, FileCheck, BadgeCheck,
  CalendarDays, MessageSquare, HelpCircle, BadgeIcon, IdCard, Home,
  Paperclip, Link2, Clock4, Timer, Hourglass, SendHorizonal, MailOpen,
  Signature, FileSignature, FileDown, FileUp, FolderOpen, Archive,
  LayoutDashboard, PieChart, LineChart, AreaChart, BarChart, TrendingUp as TrendUp,
  ArrowDownUp, SortAsc, SortDesc, RefreshCw, Loader2, Ban, Check, CheckCheck,
  Radio as RadioIcon, Gauge as GaugeIcon, Fuel, BadgeCheck as BadgeCheckIcon,
  Receipt, DollarSign, CreditCard as CreditCardIcon, Wallet as WalletIcon,
  Droplets, Inbox, Reply,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { loadGoogleMaps } from "@/lib/google-maps-loader";

// ════════════════════════════════════════════════════════════════════════════
// DESIGN TOKENS
// ════════════════════════════════════════════════════════════════════════════

const C = {
  bg: "#EDE4D4",
  card: "#FFFFFF",
  cardBorder: "#D2D2D7",
  border: "#D2D2D7",
  accent: "#7B1416",
  accentHover: "#0077ED",
  accentLight: "#F5EFE4",
  text: "#1C1917",
  textMid: "#424245",
  textMuted: "#86868B",
  textLight: "#A1A1A6",
  green: "#C4A882",
  greenLight: "#E3F9E5",
  red: "#B33A1A",
  redLight: "#FEE5E5",
  blue: "#7B1416",
  blueLight: "#F5EFE4",
  purple: "#8B6F47",
  purpleLight: "#F5E6FD",
  orange: "#C4A882",
  orangeLight: "#FFF4E5",
  gold: "#FFCC00",
  goldLight: "#FFFADC",
  teal: "#C4A882",
  tealLight: "#E6FFFE",
  amber: "#C4A882",
  amberLight: "#FFF4E5",
  pink: "#FF2D55",
  pinkLight: "#FFE8ED",
  forest: "#7B1416",
  mustard: "#C4A882",
};

const ACCENT_GRADIENT = `linear-gradient(135deg, ${C.accent} 0%, #A42022 100%)`;

// ════════════════════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════════════════════

export type StudentTabType =
  | "overview"
  | "academics"
  | "attendance"
  | "finance"
  | "health"
  | "transport"
  | "docs"
  | "timeline";

type AcademicsSubTab =
  | "summary"
  | "subjects"
  | "assessments"
  | "assignments"
  | "exams"
  | "reports"
  | "support"
  | "targets";

type FinanceSubTab =
  | "summary"
  | "invoices"
  | "payments"
  | "plans"
  | "receipts"
  | "adjustments";

interface AlertItem {
  id: string;
  type: "fee" | "attendance" | "exam" | "consent" | "document" | "health" | "assignment";
  severity: "low" | "medium" | "high";
  title: string;
  description: string;
  action?: string;
  dueDate?: string;
}

interface SubjectData {
  id: string;
  name: string;
  teacher: string;
  average: number;
  grade: string;
  trend: "up" | "down" | "stable";
  status: "strong" | "watch" | "at_risk";
  teacherRemark?: string;
  assignmentsDue: number;
  lastAssessment?: string;
}

interface AssessmentData {
  id: string;
  subject: string;
  type: "quiz" | "test" | "exam" | "project" | "oral" | "practical";
  title: string;
  date: string;
  maxScore: number;
  score?: number;
  classAverage?: number;
  weight: number;
  status: "published" | "provisional" | "pending";
  acknowledged: boolean;
  remarks?: string;
}

interface AssignmentData {
  id: string;
  subject: string;
  title: string;
  dueDate: string;
  status: "not_started" | "in_progress" | "submitted" | "late" | "missing" | "returned";
  maxScore?: number;
  score?: number;
  feedback?: string;
  attachments: number;
  resubmissionAllowed?: boolean;
}

// Demo Data
const DEMO_ALERTS: AlertItem[] = [
  { id: "a1", type: "fee", severity: "high", title: "Term 3 fees due", description: "UGX 450,000 outstanding — payment required by Jul 15", action: "Pay now", dueDate: "Jul 15, 2025" },
  { id: "a2", type: "exam", severity: "medium", title: "End of Term Exam", description: "Mathematics exam scheduled for Jul 18", action: "View schedule", dueDate: "Jul 18, 2025" },
  { id: "a3", type: "consent", severity: "medium", title: "Consent form pending", description: "Science lab trip permission required", action: "Review", dueDate: "Jul 7, 2025" },
  { id: "a4", type: "assignment", severity: "low", title: "3 assignments due", description: "Trigonometry, Lab Report, and Essay submissions pending", action: "View assignments" },
];

const DEMO_SUBJECTS: SubjectData[] = [
  { id: "s1", name: "Mathematics", teacher: "Mr. Okello", average: 87, grade: "A", trend: "up", status: "strong", teacherRemark: "Excellent improvement this term", assignmentsDue: 1, lastAssessment: "Quiz 4: 92%" },
  { id: "s2", name: "Physics", teacher: "Ms. Namugga", average: 82, grade: "A-", trend: "up", status: "strong", assignmentsDue: 0 },
  { id: "s3", name: "English", teacher: "Mrs. Byaruhanga", average: 78, grade: "B+", trend: "up", status: "watch", assignmentsDue: 1 },
  { id: "s4", name: "Biology", teacher: "Dr. Atwine", average: 91, grade: "A", trend: "up", status: "strong", assignmentsDue: 0, teacherRemark: "Outstanding lab work" },
  { id: "s5", name: "Geography", teacher: "Mr. Mugisha", average: 68, grade: "B-", trend: "down", status: "at_risk", assignmentsDue: 1, teacherRemark: "Needs to improve map reading skills" },
  { id: "s6", name: "Chemistry", teacher: "Mr. Ssekandi", average: 74, grade: "B", trend: "stable", status: "watch", assignmentsDue: 0 },
];

const DEMO_ASSESSMENTS: AssessmentData[] = [
  { id: "as1", subject: "Mathematics", type: "quiz", title: "Trigonometry Quiz", date: "Jul 2, 2025", maxScore: 20, score: 18, classAverage: 15.2, weight: 10, status: "published", acknowledged: true, remarks: "Excellent understanding of sine/cosine" },
  { id: "as2", subject: "Biology", type: "practical", title: "Enzyme Activity Lab", date: "Jun 28, 2025", maxScore: 30, score: 28, classAverage: 24, weight: 15, status: "published", acknowledged: true },
  { id: "as3", subject: "English", type: "test", title: "Essay Writing Test", date: "Jun 25, 2025", maxScore: 50, score: 42, weight: 20, status: "published", acknowledged: false },
  { id: "as4", subject: "Physics", type: "exam", title: "Mid-Term Exam", date: "Jun 20, 2025", maxScore: 100, score: 82, classAverage: 71, weight: 40, status: "published", acknowledged: true },
];

const DEMO_ASSIGNMENTS: AssignmentData[] = [
  { id: "ag1", subject: "Mathematics", title: "Trigonometry Problem Set", dueDate: "Jul 8, 2025", status: "not_started", maxScore: 20, attachments: 1 },
  { id: "ag2", subject: "Biology", title: "Lab Report: Enzyme Activity", dueDate: "Jul 10, 2025", status: "in_progress", attachments: 2 },
  { id: "ag3", subject: "Geography", title: "Essay: Climate Change Impact", dueDate: "Jul 12, 2025", status: "not_started", attachments: 0 },
  { id: "ag4", subject: "Chemistry", title: "Organic Compounds Worksheet", dueDate: "Jun 30, 2025", status: "submitted", score: 16, maxScore: 20, feedback: "Good work, check bond angles", attachments: 1 },
  { id: "ag5", subject: "English", title: "Creative Writing Entry", dueDate: "Jun 25, 2025", status: "returned", score: 38, maxScore: 50, feedback: "Strong narrative voice, work on pacing", attachments: 1, resubmissionAllowed: true },
];

const DEMO_EXAMS = [
  { id: "e1", subject: "Mathematics", date: "Jul 18, 2025", time: "9:00 AM - 12:00 PM", venue: "Hall A", seat: "A24", status: "upcoming" },
  { id: "e2", subject: "Physics", date: "Jul 19, 2025", time: "2:00 PM - 5:00 PM", venue: "Lab 2", seat: "B7", status: "upcoming" },
  { id: "e3", subject: "English", date: "Jul 20, 2025", time: "9:00 AM - 11:00 AM", venue: "Hall B", seat: "C15", status: "upcoming" },
];

// ════════════════════════════════════════════════════════════════════════════
// UTILITY COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

function fmtUGX(n: number) {
  return `UGX ${n.toLocaleString("en-UG")}`;
}

function shortDateTime(s: string) {
  return new Date(s).toLocaleDateString("en-UG", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function StatCard({ label, value, sub, icon: Icon, accent, onClick, compact }: {
  label: string; value: string | number; sub?: string; icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>; accent: string; onClick?: () => void; compact?: boolean;
}) {
  return (
    <motion.button
      onClick={onClick}
      disabled={!onClick}
      className={`relative overflow-hidden rounded-xl border p-3 text-left shadow-sm transition-all ${onClick ? "cursor-pointer hover:shadow-md" : ""} ${compact ? "p-2.5" : ""}`}
      style={{ background: C.card, borderColor: C.cardBorder }}
      whileHover={onClick ? { y: -2 } : undefined}
      whileTap={onClick ? { scale: 0.98 } : undefined}
    >
      <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: accent }} />
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className={`font-bold uppercase tracking-wider ${compact ? "text-[9px]" : "text-[10px]"}`} style={{ color: C.textMuted }}>{label}</p>
          <p className={`font-bold mt-0.5 truncate ${compact ? "text-base" : "text-lg"}`} style={{ color: C.text }}>{value}</p>
          {sub && <p className="text-[10px] mt-0.5 truncate" style={{ color: C.textMid }}>{sub}</p>}
        </div>
        <div className={`flex items-center justify-center rounded-lg shrink-0 ${compact ? "h-8 w-8" : "h-9 w-9"}`} style={{ background: `${accent}15` }}>
          <Icon className={`${compact ? "h-4 w-4" : "h-4.5 w-4.5"}`} style={{ color: accent }} />
        </div>
      </div>
    </motion.button>
  );
}

function PanelCard({ title, subtitle, icon: Icon, iconBg, children, action, className = "" }: {
  title: string; subtitle?: string; icon: React.ComponentType<{ className?: string }>; iconBg: string; children: React.ReactNode; action?: React.ReactNode; className?: string;
}) {
  return (
    <div className={`rounded-xl border overflow-hidden shadow-sm ${className}`} style={{ background: C.card, borderColor: C.cardBorder }}>
      <div className="border-b px-4 py-3 flex items-center justify-between" style={{ borderColor: C.cardBorder }}>
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: iconBg }}>
            <Icon className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-bold" style={{ color: C.text }}>{title}</p>
            {subtitle && <p className="text-[10px]" style={{ color: C.textMuted }}>{subtitle}</p>}
          </div>
        </div>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function AlertPill({ alert, onAction }: { alert: AlertItem; onAction?: () => void }) {
  const severityColors = {
    low: { bg: C.greenLight, border: C.green, text: C.green },
    medium: { bg: C.orangeLight, border: C.orange, text: C.orange },
    high: { bg: C.redLight, border: C.red, text: C.red },
  };
  const colors = severityColors[alert.severity];
  const typeIcons = {
    fee: CreditCard,
    attendance: Clock,
    exam: BookOpen,
    consent: FilePen,
    document: FileText,
    health: Heart,
    assignment: ClipboardList,
  };
  const Icon = typeIcons[alert.type] || AlertCircle;

  return (
    <motion.div
      className="flex items-start gap-3 rounded-xl border p-3"
      style={{ background: colors.bg, borderColor: `${colors.border}40` }}
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
    >
      <div className="flex items-center justify-center h-8 w-8 rounded-lg shrink-0" style={{ background: `${colors.text}20` }}>
        <Icon className="h-4 w-4" style={{ color: colors.text }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold" style={{ color: C.text }}>{alert.title}</p>
        <p className="text-[11px] mt-0.5" style={{ color: C.textMid }}>{alert.description}</p>
        {alert.dueDate && (
          <p className="text-[10px] mt-1 flex items-center gap-1" style={{ color: colors.text }}>
            <Clock className="h-3 w-3" />Due: {alert.dueDate}
          </p>
        )}
      </div>
      {alert.action && onAction && (
        <Button size="sm" variant="outline" className="h-7 text-[10px] rounded-lg shrink-0" style={{ borderColor: colors.text, color: colors.text }} onClick={onAction}>
          {alert.action}
        </Button>
      )}
    </motion.div>
  );
}

function QuickActionButton({ icon: Icon, label, onClick, color = C.accent, badge }: {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>; label: string; onClick?: () => void; color?: string; badge?: number;
}) {
  return (
    <motion.button
      onClick={onClick}
      className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border transition-all"
      style={{ background: C.card, borderColor: C.cardBorder }}
      whileHover={{ y: -2, shadow: "0 4px 12px rgba(0,0,0,0.1)" }}
      whileTap={{ scale: 0.96 }}
    >
      <div className="relative flex items-center justify-center h-9 w-9 rounded-lg" style={{ background: `${color}15` }}>
        <Icon className="h-4 w-4" style={{ color }} />
        {badge && badge > 0 && (
          <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full text-[9px] font-bold text-white grid place-items-center" style={{ background: C.red }}>
            {badge}
          </span>
        )}
      </div>
      <span className="text-[10px] font-semibold text-center leading-tight" style={{ color: C.textMid }}>{label}</span>
    </motion.button>
  );
}

function SectionHead({ title, subtitle, action }: { title: string; subtitle?: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div>
        <p className="font-bold text-sm" style={{ color: C.text }}>{title}</p>
        {subtitle && <p className="text-[10px]" style={{ color: C.textMuted }}>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// EXAM SCHEDULE MODAL — Detailed aesthetic schedule view
// ════════════════════════════════════════════════════════════════════════════

const EXAM_SCHEDULE = [
  { id: "e1", day: "Monday", date: "Jul 14", subject: "Mathematics", time: "08:00 — 10:00", duration: "2 hrs", room: "Hall A", seat: "B-12", invigilator: "Mr. Okello", color: C.blue },
  { id: "e2", day: "Monday", date: "Jul 14", subject: "English Language", time: "11:00 — 12:30", duration: "1.5 hrs", room: "Hall A", seat: "B-12", invigilator: "Mrs. Atim", color: C.green },
  { id: "e3", day: "Tuesday", date: "Jul 15", subject: "Science", time: "08:00 — 10:00", duration: "2 hrs", room: "Lab 1", seat: "C-04", invigilator: "Dr. Atwine", color: C.orange },
  { id: "e4", day: "Tuesday", date: "Jul 15", subject: "Social Studies", time: "11:00 — 12:30", duration: "1.5 hrs", room: "Hall B", seat: "B-12", invigilator: "Mr. Ssentongo", color: C.purple },
  { id: "e5", day: "Wednesday", date: "Jul 16", subject: "Religious Education", time: "08:00 — 09:30", duration: "1.5 hrs", room: "Hall A", seat: "B-12", invigilator: "Mrs. Nakato", color: C.teal },
  { id: "e6", day: "Thursday", date: "Jul 17", subject: "Art & Craft", time: "08:00 — 10:00", duration: "2 hrs", room: "Art Studio", seat: "—", invigilator: "Ms. Kemigisha", color: C.pink },
  { id: "e7", day: "Friday", date: "Jul 18", subject: "Mathematics (Paper 2)", time: "08:00 — 10:00", duration: "2 hrs", room: "Hall A", seat: "B-12", invigilator: "Mr. Okello", color: C.blue },
];

function ExamScheduleModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null;
  const byDay = EXAM_SCHEDULE.reduce((acc, e) => {
    if (!acc[e.date]) acc[e.date] = { day: e.day, date: e.date, exams: [] };
    acc[e.date].exams.push(e);
    return acc;
  }, {} as Record<string, { day: string; date: string; exams: typeof EXAM_SCHEDULE }>);
  const days = Object.values(byDay);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(16px)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 24 }}
        transition={{ type: "spring", stiffness: 400, damping: 32 }}
        className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative overflow-hidden p-6 text-white" style={{ background: "linear-gradient(135deg, #0B2A1F, #00897B)" }}>
          <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-20" style={{ background: "white" }} />
          <div className="relative flex items-start justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] backdrop-blur">
                <Calendar className="h-3 w-3" /> End of Term 3 Exams
              </div>
              <h3 className="mt-3 text-2xl font-bold" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Exam Schedule</h3>
              <p className="mt-1 text-xs text-white/70">Jul 14 — Jul 18, 2025 · Greenhill Academy</p>
            </div>
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/10">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {days.map((day) => (
            <div key={day.date}>
              <div className="flex items-center gap-2 mb-2.5">
                <div className="h-8 w-8 rounded-xl flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ background: C.accent }}>
                  {day.date.split(" ")[1]}
                </div>
                <div>
                  <p className="text-sm font-bold" style={{ color: C.text }}>{day.day}</p>
                  <p className="text-[10px]" style={{ color: C.textMuted }}>{day.date}, 2025</p>
                </div>
                <div className="ml-auto text-[10px] font-semibold px-2.5 py-1 rounded-full" style={{ background: C.bg, color: C.textMuted }}>
                  {day.exams.length} exam{day.exams.length > 1 ? "s" : ""}
                </div>
              </div>
              <div className="space-y-2">
                {day.exams.map((exam) => (
                  <motion.div
                    key={exam.id}
                    initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                    className="flex items-stretch gap-3 rounded-2xl p-3.5" style={{ border: `1px solid ${C.cardBorder}`, background: C.bg }}
                  >
                    <div className="w-1.5 rounded-full shrink-0" style={{ background: exam.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-bold truncate" style={{ color: C.text }}>{exam.subject}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]" style={{ color: C.textMuted }}>
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {exam.time}</span>
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {exam.room}</span>
                        <span className="flex items-center gap-1"><User className="h-3 w-3" /> Seat {exam.seat}</span>
                        <span className="flex items-center gap-1"><Award className="h-3 w-3" /> {exam.duration}</span>
                      </div>
                      <p className="text-[10px] mt-1.5" style={{ color: C.textLight }}>Invigilator: {exam.invigilator}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}

          {/* Footer tip */}
          <div className="rounded-xl p-3 flex items-center gap-2.5" style={{ background: `${C.accent}08`, border: `1px solid ${C.accent}22` }}>
            <Bell className="h-4 w-4 shrink-0" style={{ color: C.accent }} />
            <p className="text-xs" style={{ color: C.textMid }}>Arrive 15 minutes before each exam. Bring your student ID and required stationery.</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// OVERVIEW TAB — Fast scan layer (<10s)
// ════════════════════════════════════════════════════════════════════════════

export interface OverviewTabCallbacks {
  onPayFees?: () => void;
  onMessageTeacher?: () => void;
  onViewReports?: () => void;
  onViewTransport?: () => void;
  onViewCalendar?: () => void;
  onApproveConsent?: () => void;
  onViewHealth?: () => void;
  onUploadDocument?: () => void;
  onViewDocuments?: () => void;
  onViewSchedule?: () => void;
}

export function OverviewTab({ studentId, alerts, ...callbacks }: {
  studentId: string;
  alerts?: AlertItem[];
} & OverviewTabCallbacks) {
  const displayAlerts = alerts || DEMO_ALERTS;
  const unreadMessages = 3;
  const pendingApprovals = 2;
  const [showSchedule, setShowSchedule] = useState(false);

  const handleAction = (action: string, callback?: () => void) => {
    if (callback) {
      callback();
    } else {
      toast.info(`${action}...`);
    }
  };

  return (
    <div className="space-y-4">
      {/* Status Banner */}
      <motion.div
        className="rounded-2xl p-4"
        style={{ background: ACCENT_GRADIENT }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full flex items-center justify-center text-white text-xl shrink-0" style={{ background: "rgba(255,255,255,0.2)" }}>
            EN
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-white font-bold text-lg truncate">Emma Nakamya</p>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold text-white" style={{ background: "rgba(255,255,255,0.2)" }}>Primary 6</span>
            </div>
            <p className="text-white/80 text-xs mt-0.5">Greenhill Academy · Term 3 · 2025</p>
          </div>
          <div className="text-right shrink-0">
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: "rgba(255,255,255,0.2)" }}>
              <div className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-white text-xs font-semibold">In Class</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-2">
        <StatCard label="Attendance" value="96%" sub="This term" icon={CheckCircle} accent={C.green} compact />
        <StatCard label="Average" value="82%" sub="Term 3" icon={Trophy} accent={C.gold} compact />
        <StatCard label="Balance" value="UGX 450K" sub="Outstanding" icon={CreditCard} accent={C.orange} compact />
        <StatCard label="Rank" value="3rd" sub="Of 42" icon={Award} accent={C.purple} compact />
      </div>

      {/* Alerts Section - What needs attention now */}
      {displayAlerts.length > 0 && (
        <PanelCard title="Needs Attention" subtitle={`${displayAlerts.length} items`} icon={AlertTriangle} iconBg={C.redLight}>
          <div className="space-y-2">
            {displayAlerts.slice(0, 3).map((alert) => {
              const action = alert.action ?? "";
              const actionHandler =
                action === "Pay now" ? () => handleAction(action, callbacks.onPayFees) :
                action === "View schedule" ? () => setShowSchedule(true) :
                action === "Review" ? () => handleAction(action, callbacks.onApproveConsent) :
                action === "View assignments" ? () => handleAction(action) :
                () => handleAction(action);
              return <AlertPill key={alert.id} alert={alert} onAction={actionHandler} />;
            })}
            {displayAlerts.length > 3 && (
              <button className="w-full text-center text-xs font-semibold py-2" style={{ color: C.accent }}>
                +{displayAlerts.length - 3} more alerts
              </button>
            )}
          </div>
        </PanelCard>
      )}

      {/* Quick Actions Grid */}
      <div>
        <SectionHead title="Quick Actions" />
        <div className="grid grid-cols-4 gap-2">
          <QuickActionButton icon={CreditCard} label="Pay Fees" color={C.green} onClick={() => handleAction("Opening payment", callbacks.onPayFees)} badge={1} />
          <QuickActionButton icon={MessageCircle} label="Message" color={C.blue} onClick={() => handleAction("Opening messages", callbacks.onMessageTeacher)} badge={unreadMessages} />
          <QuickActionButton icon={Download} label="Reports" color={C.purple} onClick={() => handleAction("Opening reports", callbacks.onViewReports)} />
          <QuickActionButton icon={Bus} label="Transport" color={C.orange} onClick={() => handleAction("Opening transport", callbacks.onViewTransport)} />
          <QuickActionButton icon={Calendar} label="Calendar" color={C.teal} onClick={() => handleAction("Opening calendar", callbacks.onViewCalendar)} />
          <QuickActionButton icon={FilePen} label="Consent" color={C.amber} badge={pendingApprovals} onClick={() => handleAction("Opening consents", callbacks.onApproveConsent)} />
          <QuickActionButton icon={Heart} label="Health" color={C.pink} onClick={() => handleAction("Opening health", callbacks.onViewHealth)} />
          <QuickActionButton icon={FileText} label="Docs" color={C.textMid} onClick={() => handleAction("Opening documents", callbacks.onViewDocuments)} />
        </div>
      </div>

      <ExamScheduleModal open={showSchedule} onClose={() => setShowSchedule(false)} />

      {/* Today's Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <PanelCard title="Today's Status" icon={Sun} iconBg={C.goldLight}>
          <div className="space-y-2">
            {[
              { label: "Arrived", value: "7:05 AM", icon: Clock, color: C.green },
              { label: "Lunch", value: "Taken 12:30 PM", icon: Utensils, color: C.orange },
              { label: "Last Seen", value: "Classroom Block A", icon: MapPin, color: C.blue },
              { label: "Pickup", value: "4:30 PM", icon: Bus, color: C.purple },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <item.icon className="h-3.5 w-3.5" style={{ color: item.color }} />
                  <span className="text-xs" style={{ color: C.textMuted }}>{item.label}</span>
                </div>
                <span className="text-xs font-semibold" style={{ color: C.text }}>{item.value}</span>
              </div>
            ))}
          </div>
        </PanelCard>

        <PanelCard title="Guardian & Emergency" icon={Shield} iconBg={C.greenLight}>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ background: C.accent }}>SN</div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate" style={{ color: C.text }}>Sarah Nakamya</p>
                <p className="text-[10px]" style={{ color: C.textMuted }}>Primary Guardian · Mother</p>
              </div>
              <div className="flex gap-1">
                <button className="h-7 w-7 rounded-lg flex items-center justify-center" style={{ background: C.greenLight }}>
                  <Phone className="h-3 w-3" style={{ color: C.green }} />
                </button>
                <button className="h-7 w-7 rounded-lg flex items-center justify-center" style={{ background: C.blueLight }}>
                  <MessageCircle className="h-3 w-3" style={{ color: C.blue }} />
                </button>
              </div>
            </div>
            <div className="border-t pt-2" style={{ borderColor: C.border }}>
              <p className="text-[10px] font-semibold mb-1" style={{ color: C.textMuted }}>EMERGENCY CONTACT</p>
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0" style={{ background: C.red }}>JM</div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold truncate" style={{ color: C.text }}>John Mukasa</p>
                  <p className="text-[9px]" style={{ color: C.textMuted }}>Uncle · +256 701 234 567</p>
                </div>
              </div>
            </div>
          </div>
        </PanelCard>
      </div>

      {/* Pending Messages Preview */}
      <PanelCard title="Messages" subtitle={`${unreadMessages} unread`} icon={MessageCircle} iconBg={C.blueLight} action={
        <Button size="sm" variant="ghost" className="h-7 text-xs" style={{ color: C.accent }} onClick={callbacks.onMessageTeacher}>View All</Button>
      }>
        <div className="space-y-2">
          {[
            { from: "Mr. Okello (Math)", preview: "Emma performed excellently in the quiz...", time: "2h ago", unread: true },
            { from: "School Office", preview: "Reminder: Parent-Teacher meeting...", time: "1d ago", unread: true },
            { from: "Dr. Atwine (Biology)", preview: "Lab report feedback is ready...", time: "2d ago", unread: false },
          ].slice(0, 2).map((msg, i) => (
            <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg" style={{ background: msg.unread ? C.blueLight : C.bg }}>
              {msg.unread && <div className="h-2 w-2 rounded-full bg-blue-500 shrink-0 mt-1.5" />}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate" style={{ color: C.text }}>{msg.from}</p>
                <p className="text-[10px] truncate" style={{ color: C.textMuted }}>{msg.preview}</p>
              </div>
              <span className="text-[9px] shrink-0" style={{ color: C.textLight }}>{msg.time}</span>
            </div>
          ))}
        </div>
      </PanelCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ACADEMICS TAB — Deep academic story
// ════════════════════════════════════════════════════════════════════════════

export function AcademicsTab({ studentId, subjects, assessments, assignments }: {
  studentId: string;
  subjects: SubjectData[];
  assessments: AssessmentData[];
  assignments: AssignmentData[];
}) {
  const [subTab, setSubTab] = useState<AcademicsSubTab>("summary");
  const averageScore = subjects.reduce((s, sub) => s + sub.average, 0) / subjects.length;
  const atRiskSubjects = subjects.filter(s => s.status === "at_risk");
  const pendingAssignments = assignments.filter(a => a.status === "not_started" || a.status === "in_progress");

  const SUB_TABS: { k: AcademicsSubTab; label: string; icon: React.ComponentType<{ className?: string }>; badge?: number }[] = [
    { k: "summary", label: "Summary", icon: LayoutDashboard },
    { k: "subjects", label: "Subjects", icon: BookOpen },
    { k: "assessments", label: "Assessments", icon: ClipboardList },
    { k: "assignments", label: "Assignments", icon: PenTool, badge: pendingAssignments.length },
    { k: "exams", label: "Exams", icon: GraduationCap },
    { k: "reports", label: "Reports", icon: FileText },
    { k: "support", label: "Support", icon: Heart, badge: atRiskSubjects.length },
    { k: "targets", label: "Targets", icon: Target },
  ];

  return (
    <div className="space-y-4">
      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-2">
        <StatCard label="Average" value={`${Math.round(averageScore)}%`} sub="Term 3" icon={TrendingUp} accent={C.green} compact />
        <StatCard label="Rank" value="3rd" sub="Of 42" icon={Trophy} accent={C.gold} compact />
        <StatCard label="Subjects" value={String(subjects.length)} sub="6 enrolled" icon={BookOpen} accent={C.blue} compact />
        <StatCard label="GPA" value="3.85" sub="Weighted" icon={Award} accent={C.purple} compact />
      </div>

      {/* Sub-tab Navigation */}
      <div className="flex gap-1.5 overflow-x-auto [scrollbar-width:none] -mx-4 px-4">
        {SUB_TABS.map((tab) => {
          const isActive = subTab === tab.k;
          return (
            <button
              key={tab.k}
              onClick={() => setSubTab(tab.k)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all shrink-0 ${isActive ? "" : "hover:bg-slate-50"}`}
              style={{ background: isActive ? C.accent : "transparent", color: isActive ? "white" : C.textMid }}
            >
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
              {tab.badge !== undefined && tab.badge > 0 && (
                <span className="ml-1 h-4 min-w-[16px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: isActive ? "rgba(255,255,255,0.3)" : C.redLight, color: isActive ? "white" : C.red }}>
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Sub-tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={subTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.15 }}
        >
          {subTab === "summary" && (
            <div className="space-y-4">
              {/* Performance Trend */}
              <PanelCard title="Performance Trend" subtitle="Term comparison" icon={TrendingUp} iconBg={C.greenLight}>
                <div className="flex items-center gap-4">
                  <div className="text-center flex-1 p-3 rounded-xl" style={{ background: C.bg }}>
                    <p className="text-2xl font-bold" style={{ color: C.text }}>78%</p>
                    <p className="text-[10px]" style={{ color: C.textMuted }}>Term 2</p>
                  </div>
                  <div className="flex flex-col items-center">
                    <ArrowRight className="h-5 w-5" style={{ color: C.green }} />
                    <span className="text-xs font-bold" style={{ color: C.green }}>+4%</span>
                  </div>
                  <div className="text-center flex-1 p-3 rounded-xl" style={{ background: C.greenLight }}>
                    <p className="text-2xl font-bold" style={{ color: C.green }}>{Math.round(averageScore)}%</p>
                    <p className="text-[10px]" style={{ color: C.green }}>Term 3</p>
                  </div>
                </div>
              </PanelCard>

              {/* Subject Status Grid */}
              <PanelCard title="Subject Status" icon={BookOpen} iconBg={C.blueLight}>
                <div className="grid grid-cols-3 gap-2">
                  {subjects.map((sub) => {
                    const statusColors = { strong: C.green, watch: C.orange, at_risk: C.red };
                    const color = statusColors[sub.status];
                    return (
                      <div key={sub.id} className="p-2.5 rounded-lg text-center" style={{ background: `${color}10` }}>
                        <p className="text-[10px] font-semibold truncate" style={{ color: C.textMid }}>{sub.name}</p>
                        <p className="text-lg font-bold" style={{ color }}>{sub.average}%</p>
                        <div className="flex items-center justify-center gap-1 mt-1">
                          {sub.trend === "up" && <TrendingUp className="h-3 w-3" style={{ color: C.green }} />}
                          {sub.trend === "down" && <TrendingDown className="h-3 w-3" style={{ color: C.red }} />}
                          {sub.trend === "stable" && <Minus className="h-3 w-3" style={{ color: C.textMuted }} />}
                          <span className="text-[9px] font-semibold" style={{ color: sub.trend === "up" ? C.green : sub.trend === "down" ? C.red : C.textMuted }}>
                            {sub.grade}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </PanelCard>

              {/* Latest Results */}
              <PanelCard title="Latest Assessments" icon={ClipboardList} iconBg={C.purpleLight}>
                <div className="space-y-2">
                  {assessments.slice(0, 3).map((asmt) => (
                    <div key={asmt.id} className="flex items-center justify-between p-2.5 rounded-lg" style={{ background: C.bg }}>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold truncate" style={{ color: C.text }}>{asmt.title}</p>
                        <p className="text-[10px]" style={{ color: C.textMuted }}>{asmt.subject}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold" style={{ color: asmt.score && asmt.score >= (asmt.maxScore * 0.8) ? C.green : asmt.score && asmt.score >= (asmt.maxScore * 0.6) ? C.orange : C.red }}>
                          {asmt.score}/{asmt.maxScore}
                        </p>
                        <p className="text-[9px]" style={{ color: C.textMuted }}>{asmt.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </PanelCard>
            </div>
          )}

          {subTab === "subjects" && (
            <div className="space-y-3">
              {subjects.map((sub) => {
                const statusColors = { strong: C.green, watch: C.orange, at_risk: C.red };
                const color = statusColors[sub.status];
                const [expanded, setExpanded] = useState(false);
                return (
                  <motion.div
                    key={sub.id}
                    className="rounded-xl border overflow-hidden"
                    style={{ background: C.card, borderColor: C.cardBorder }}
                  >
                    <button
                      onClick={() => setExpanded(!expanded)}
                      className="w-full flex items-center gap-3 p-4 text-left"
                    >
                      <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}15` }}>
                        <BookOpen className="h-5 w-5" style={{ color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold truncate" style={{ color: C.text }}>{sub.name}</p>
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold" style={{ background: `${color}15`, color }}>{sub.grade}</span>
                          {sub.status === "at_risk" && <AlertTriangle className="h-3.5 w-3.5" style={{ color: C.red }} />}
                        </div>
                        <p className="text-xs" style={{ color: C.textMuted }}>{sub.teacher}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xl font-bold" style={{ color }}>{sub.average}%</p>
                        <div className="flex items-center gap-1">
                          {sub.trend === "up" && <TrendingUp className="h-3 w-3" style={{ color: C.green }} />}
                          {sub.trend === "down" && <TrendingDown className="h-3 w-3" style={{ color: C.red }} />}
                          {sub.trend === "stable" && <Minus className="h-3 w-3" style={{ color: C.textMuted }} />}
                        </div>
                      </div>
                      <ChevronDown className={`h-5 w-5 transition-transform ${expanded ? "rotate-180" : ""}`} style={{ color: C.textMuted }} />
                    </button>
                    <AnimatePresence>
                      {expanded && (
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: "auto" }}
                          exit={{ height: 0 }}
                          className="border-t overflow-hidden"
                          style={{ borderColor: C.cardBorder }}
                        >
                          <div className="p-4 space-y-3">
                            {/* Progress bar */}
                            <div>
                              <div className="flex justify-between text-xs mb-1">
                                <span style={{ color: C.textMuted }}>Average Score</span>
                                <span className="font-bold" style={{ color: C.text }}>{sub.average}%</span>
                              </div>
                              <div className="h-2 rounded-full overflow-hidden" style={{ background: C.bg }}>
                                <div className="h-full rounded-full" style={{ width: `${sub.average}%`, background: color }} />
                              </div>
                            </div>
                            {sub.teacherRemark && (
                              <div className="p-2.5 rounded-lg" style={{ background: `${color}08` }}>
                                <p className="text-[10px] font-semibold mb-0.5" style={{ color: C.textMuted }}>Teacher's Note</p>
                                <p className="text-xs" style={{ color: C.text }}>{sub.teacherRemark}</p>
                              </div>
                            )}
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" className="flex-1 h-8 text-xs">
                                <MessageCircle className="h-3 w-3 mr-1.5" />Message Teacher
                              </Button>
                              <Button size="sm" variant="outline" className="h-8 text-xs">
                                <Download className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          )}

          {subTab === "assessments" && (
            <div className="space-y-4">
              {/* Upcoming */}
              <PanelCard title="Upcoming" icon={Clock} iconBg={C.orangeLight}>
                <div className="p-4 rounded-xl text-center" style={{ background: C.bg }}>
                  <Calendar className="h-8 w-8 mx-auto mb-2" style={{ color: C.textMuted }} />
                  <p className="text-sm font-semibold" style={{ color: C.text }}>No upcoming assessments</p>
                  <p className="text-xs" style={{ color: C.textMuted }}>Next exam: Jul 18, 2025</p>
                </div>
              </PanelCard>
              {/* Recent */}
              <PanelCard title="Recent Results" icon={ClipboardList} iconBg={C.purpleLight}>
                <div className="space-y-2">
                  {assessments.filter(a => a.status === "published").map((asmt) => (
                    <div key={asmt.id} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: C.bg }}>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold truncate" style={{ color: C.text }}>{asmt.title}</p>
                        <p className="text-[10px]" style={{ color: C.textMuted }}>{asmt.subject} · {asmt.date}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold" style={{ color: asmt.score && asmt.score >= (asmt.maxScore * 0.8) ? C.green : asmt.score && asmt.score >= (asmt.maxScore * 0.6) ? C.orange : C.red }}>
                          {asmt.score}/{asmt.maxScore}
                        </p>
                        {asmt.classAverage && (
                          <p className="text-[9px]" style={{ color: C.textMuted }}>Class avg: {asmt.classAverage}</p>
                        )}
                      </div>
                      {asmt.acknowledged ? (
                        <CheckCircle className="h-4 w-4" style={{ color: C.green }} />
                      ) : (
                        <Button size="sm" variant="outline" className="h-7 text-[10px]">Acknowledge</Button>
                      )}
                    </div>
                  ))}
                </div>
              </PanelCard>
            </div>
          )}

          {subTab === "assignments" && (
            <div className="space-y-4">
              {/* Due Now */}
              <PanelCard title="Due Now" subtitle={`${pendingAssignments.length} pending`} icon={Clock} iconBg={C.redLight}>
                <div className="space-y-2">
                  {assignments.filter(a => a.status === "not_started" || a.status === "in_progress").map((asgn) => (
                    <div key={asgn.id} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: C.bg }}>
                      <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${asgn.status === "in_progress" ? "animate-pulse" : ""}`} style={{ background: asgn.status === "in_progress" ? C.orangeLight : C.redLight }}>
                        <PenTool className="h-4 w-4" style={{ color: asgn.status === "in_progress" ? C.orange : C.red }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold truncate" style={{ color: C.text }}>{asgn.title}</p>
                        <p className="text-[10px]" style={{ color: C.textMuted }}>{asgn.subject} · Due: {asgn.dueDate}</p>
                      </div>
                      <span className="px-2 py-1 rounded-lg text-[10px] font-semibold shrink-0" style={{ background: asgn.status === "in_progress" ? C.orangeLight : C.redLight, color: asgn.status === "in_progress" ? C.orange : C.red }}>
                        {asgn.status === "in_progress" ? "In Progress" : "Not Started"}
                      </span>
                    </div>
                  ))}
                </div>
              </PanelCard>
              {/* Returned with feedback */}
              <PanelCard title="Returned with Feedback" icon={CheckCircle} iconBg={C.greenLight}>
                <div className="space-y-2">
                  {assignments.filter(a => a.status === "returned").map((asgn) => (
                    <div key={asgn.id} className="p-3 rounded-xl" style={{ background: C.bg }}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold" style={{ color: C.text }}>{asgn.title}</p>
                        <span className="text-sm font-bold" style={{ color: C.green }}>{asgn.score}/{asgn.maxScore}</span>
                      </div>
                      <div className="p-2 rounded-lg" style={{ background: C.greenLight }}>
                        <p className="text-[10px]" style={{ color: C.green }}>{asgn.feedback}</p>
                      </div>
                      {asgn.resubmissionAllowed && (
                        <Button size="sm" className="w-full h-8 mt-2 text-xs">Resubmit</Button>
                      )}
                    </div>
                  ))}
                </div>
              </PanelCard>
            </div>
          )}

          {subTab === "exams" && (
            <div className="space-y-4">
              <PanelCard title="Exam Schedule" icon={Calendar} iconBg={C.purpleLight}>
                <div className="space-y-2">
                  {DEMO_EXAMS.map((exam) => (
                    <div key={exam.id} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: C.bg }}>
                      <div className="text-center shrink-0" style={{ minWidth: "60px" }}>
                        <p className="text-lg font-bold" style={{ color: C.text }}>{exam.date.split(", ")[1]}</p>
                        <p className="text-[10px]" style={{ color: C.textMuted }}>{exam.date.split(", ")[0]}</p>
                      </div>
                      <div className="h-10 w-px" style={{ background: C.border }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold" style={{ color: C.text }}>{exam.subject}</p>
                        <p className="text-[10px]" style={{ color: C.textMuted }}>{exam.time} · {exam.venue}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[10px]" style={{ color: C.textMuted }}>Seat</p>
                        <p className="text-sm font-bold" style={{ color: C.accent }}>{exam.seat}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </PanelCard>
            </div>
          )}

          {subTab === "reports" && (
            <div className="space-y-4">
              <PanelCard title="Report Cards" icon={FileText} iconBg={C.blueLight}>
                <div className="space-y-2">
                  {[
                    { term: "Term 3 2025", status: "draft", date: "Jul 25, 2025" },
                    { term: "Term 2 2025", status: "available", date: "May 15, 2025", viewed: true },
                    { term: "Term 1 2025", status: "available", date: "Feb 20, 2025", viewed: true },
                  ].map((report, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: C.bg }}>
                      <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: report.status === "available" ? C.greenLight : C.orangeLight }}>
                        <FileText className="h-5 w-5" style={{ color: report.status === "available" ? C.green : C.orange }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold" style={{ color: C.text }}>{report.term}</p>
                        <p className="text-[10px]" style={{ color: C.textMuted }}>Released: {report.date}</p>
                      </div>
                      {report.status === "available" ? (
                        <Button size="sm" variant="outline" className="h-8 text-xs">
                          <Download className="h-3 w-3 mr-1.5" />Download
                        </Button>
                      ) : (
                        <span className="px-2 py-1 rounded-lg text-[10px] font-semibold" style={{ background: C.orangeLight, color: C.orange }}>Processing</span>
                      )}
                    </div>
                  ))}
                </div>
              </PanelCard>
            </div>
          )}

          {subTab === "support" && (
            <div className="space-y-4">
              {atRiskSubjects.length > 0 && (
                <PanelCard title="Subjects of Concern" icon={AlertTriangle} iconBg={C.redLight}>
                  <div className="space-y-2">
                    {atRiskSubjects.map((sub) => (
                      <div key={sub.id} className="p-3 rounded-xl" style={{ background: C.redLight }}>
                        <p className="text-xs font-semibold" style={{ color: C.text }}>{sub.name} — {sub.average}%</p>
                        <p className="text-[10px] mt-1" style={{ color: C.red }}>{sub.teacherRemark}</p>
                      </div>
                    ))}
                  </div>
                </PanelCard>
              )}
              <PanelCard title="Intervention Plans" icon={Heart} iconBg={C.purpleLight}>
                <div className="p-4 rounded-xl text-center" style={{ background: C.bg }}>
                  <Heart className="h-8 w-8 mx-auto mb-2" style={{ color: C.textMuted }} />
                  <p className="text-sm font-semibold" style={{ color: C.text }}>No active interventions</p>
                  <p className="text-xs" style={{ color: C.textMuted }}>Your child is on track</p>
                </div>
              </PanelCard>
            </div>
          )}

          {subTab === "targets" && (
            <div className="space-y-4">
              <PanelCard title="Academic Goals" icon={Target} iconBg={C.greenLight}>
                <div className="space-y-3">
                  {[
                    { goal: "Improve Mathematics to 90%+", progress: 87, target: 90, color: C.green },
                    { goal: "Complete all assignments on time", progress: 80, target: 100, color: C.blue },
                    { goal: "Maintain B+ or higher in all subjects", progress: 75, target: 100, color: C.purple },
                  ].map((t, i) => (
                    <div key={i} className="p-3 rounded-xl" style={{ background: C.bg }}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold" style={{ color: C.text }}>{t.goal}</p>
                        <span className="text-xs font-bold" style={{ color: t.color }}>{t.progress}%</span>
                      </div>
                      <div className="h-2 rounded-full overflow-hidden" style={{ background: C.border }}>
                        <div className="h-full rounded-full" style={{ width: `${t.progress}%`, background: t.color }} />
                      </div>
                    </div>
                  ))}
                </div>
              </PanelCard>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ATTENDANCE TAB — Pattern recognition
// ════════════════════════════════════════════════════════════════════════════

export function AttendanceTab({ studentId }: { studentId: string }) {
  const [viewMode, setViewMode] = useState<"summary" | "heatmap" | "history">("summary");

  // Generate attendance heatmap data
  const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
  const WEEKS = ["Week 1", "Week 2", "Week 3", "Week 4", "Week 5", "Week 6"];
  const ATTENDANCE_GRID = WEEKS.map(() =>
    DAYS.map(() => {
      const r = Math.random();
      if (r > 0.95) return { status: "A", color: C.red };
      if (r > 0.85) return { status: "L", color: C.orange };
      return { status: "P", color: C.green };
    })
  );

  const stats = {
    present: 45,
    absent: 2,
    late: 3,
    total: 50,
    percentage: 90,
    currentStreak: 12,
    bestStreak: 23,
  };

  return (
    <div className="space-y-4">
      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-2">
        <StatCard label="Attendance" value={`${stats.percentage}%`} sub={`${stats.present}/${stats.total} days`} icon={CheckCircle} accent={C.green} compact />
        <StatCard label="Present" value={String(stats.present)} sub="Days" icon={CheckCircle} accent={C.green} compact />
        <StatCard label="Late" value={String(stats.late)} sub="Arrivals" icon={Clock} accent={C.orange} compact />
        <StatCard label="Streak" value={String(stats.currentStreak)} sub="Days present" icon={Flame} accent={C.red} compact />
      </div>

      {/* View Toggle */}
      <div className="flex gap-2">
        {(["summary", "heatmap", "history"] as const).map((v) => (
          <button
            key={v}
            onClick={() => setViewMode(v)}
            className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
            style={{ background: viewMode === v ? C.accent : C.bg, color: viewMode === v ? "white" : C.textMuted }}
          >
            {v.charAt(0).toUpperCase() + v.slice(1)}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={viewMode} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          {viewMode === "summary" && (
            <div className="space-y-4">
              {/* Today's Status */}
              <PanelCard title="Today's Status" icon={Sun} iconBg={C.goldLight}>
                <div className="flex items-center gap-4">
                  <div className="flex-1 p-4 rounded-xl" style={{ background: C.greenLight }}>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" style={{ color: C.green }} />
                      <span className="text-sm font-bold" style={{ color: C.green }}>Present</span>
                    </div>
                    <p className="text-xs mt-1" style={{ color: C.textMuted }}>Arrived: 7:05 AM</p>
                  </div>
                  <div className="text-center">
                    <Clock className="h-8 w-8 mx-auto" style={{ color: C.textMuted }} />
                    <p className="text-[10px] mt-1" style={{ color: C.textMuted }}>Expected dismissal<br />4:30 PM</p>
                  </div>
                </div>
              </PanelCard>

              {/* Streaks */}
              <PanelCard title="Streaks & Patterns" icon={Flame} iconBg={C.redLight}>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-xl text-center" style={{ background: C.greenLight }}>
                    <Flame className="h-6 w-6 mx-auto" style={{ color: C.green }} />
                    <p className="text-xl font-bold mt-1" style={{ color: C.green }}>{stats.currentStreak}</p>
                    <p className="text-[10px]" style={{ color: C.textMuted }}>Current Streak</p>
                  </div>
                  <div className="p-3 rounded-xl text-center" style={{ background: C.goldLight }}>
                    <Trophy className="h-6 w-6 mx-auto" style={{ color: C.gold }} />
                    <p className="text-xl font-bold mt-1" style={{ color: C.gold }}>{stats.bestStreak}</p>
                    <p className="text-[10px]" style={{ color: C.textMuted }}>Best Streak</p>
                  </div>
                </div>
              </PanelCard>
            </div>
          )}

          {viewMode === "heatmap" && (
            <PanelCard title="Attendance Heatmap" subtitle="Term 3 2025" icon={Calendar} iconBg={C.blueLight}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="text-[10px] font-semibold p-1" style={{ color: C.textMuted }}></th>
                      {DAYS.map((d) => (
                        <th key={d} className="text-[10px] font-semibold p-1 text-center" style={{ color: C.textMuted }}>{d}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {WEEKS.map((week, wi) => (
                      <tr key={week}>
                        <td className="text-[9px] font-semibold p-1 pr-2" style={{ color: C.textMuted }}>{week}</td>
                        {ATTENDANCE_GRID[wi].map((cell, di) => (
                          <td key={di} className="p-1">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold" style={{ background: `${cell.color}30`, color: cell.color }}>
                              {cell.status}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-center gap-4 mt-4 pt-3 border-t" style={{ borderColor: C.border }}>
                {[
                  { label: "Present", color: C.green },
                  { label: "Late", color: C.orange },
                  { label: "Absent", color: C.red },
                ].map((l) => (
                  <div key={l.label} className="flex items-center gap-1.5">
                    <div className="h-3 w-3 rounded" style={{ background: l.color }} />
                    <span className="text-[10px]" style={{ color: C.textMuted }}>{l.label}</span>
                  </div>
                ))}
              </div>
            </PanelCard>
          )}

          {viewMode === "history" && (
            <PanelCard title="Attendance Log" icon={History} iconBg={C.purpleLight}>
              <div className="space-y-2">
                {[
                  { date: "Jul 4, Fri", status: "Present", time: "7:05 AM", color: C.green },
                  { date: "Jul 3, Thu", status: "Present", time: "7:08 AM", color: C.green },
                  { date: "Jul 2, Wed", status: "Late", time: "8:15 AM", note: "Traffic delay", color: C.orange },
                  { date: "Jul 1, Tue", status: "Present", time: "6:58 AM", color: C.green },
                  { date: "Jun 30, Mon", status: "Absent", note: "Doctor's appointment", color: C.red },
                ].map((entry, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: C.bg }}>
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${i === 0 ? "animate-pulse" : ""}`} style={{ background: `${entry.color}15` }}>
                      {entry.status === "Present" && <CheckCircle className="h-4 w-4" style={{ color: entry.color }} />}
                      {entry.status === "Late" && <Clock className="h-4 w-4" style={{ color: entry.color }} />}
                      {entry.status === "Absent" && <XCircle className="h-4 w-4" style={{ color: entry.color }} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold" style={{ color: C.text }}>{entry.date}</p>
                      {entry.note && <p className="text-[10px]" style={{ color: C.textMuted }}>{entry.note}</p>}
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-semibold" style={{ color: entry.color }}>{entry.status}</p>
                      {entry.time && <p className="text-[10px]" style={{ color: C.textMuted }}>{entry.time}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </PanelCard>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// FINANCE TAB — Line-item clarity
// ════════════════════════════════════════════════════════════════════════════

export function FinanceTab({ studentId, onPayFees }: { studentId: string; onPayFees?: () => void }) {
  const [subTab, setSubTab] = useState<"summary" | "invoices" | "payments">("summary");

  const totalFees = 3500000;
  const paid = 2800000;
  const outstanding = totalFees - paid;

  const invoices = [
    { id: "INV-001", label: "Term 3 Tuition", amount: 2500000, status: "partial", dueDate: "Aug 15, 2025" },
    { id: "INV-002", label: "Boarding Fee", amount: 800000, status: "paid", dueDate: "Jul 1, 2025" },
    { id: "INV-003", label: "Activity Fee", amount: 200000, status: "unpaid", dueDate: "Jul 15, 2025" },
  ];

  const payments = [
    { id: "PAY-001", amount: 1500000, method: "Mobile Money", ref: "MTN123456", date: "Jun 15, 2025" },
    { id: "PAY-002", amount: 800000, method: "Bank Transfer", ref: "STB789012", date: "Jul 1, 2025" },
    { id: "PAY-003", amount: 500000, method: "Card", ref: "VISA345678", date: "Jul 3, 2025" },
  ];

  return (
    <div className="space-y-4">
      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-2">
        <StatCard label="Total Due" value={fmtUGX(totalFees)} sub="Term 3" icon={Wallet} accent={C.blue} compact />
        <StatCard label="Paid" value={fmtUGX(paid)} sub={`${Math.round((paid / totalFees) * 100)}% completed`} icon={CheckCircle} accent={C.green} compact />
        <StatCard label="Balance" value={fmtUGX(outstanding)} sub="Outstanding" icon={AlertCircle} accent={outstanding > 0 ? C.red : C.green} compact />
      </div>

      {/* Progress Bar */}
      <div className="rounded-xl p-4" style={{ background: C.card, border: `1px solid ${C.cardBorder}` }}>
        <div className="flex justify-between text-xs mb-2">
          <span style={{ color: C.textMuted }}>Payment Progress</span>
          <span className="font-bold" style={{ color: C.green }}>{Math.round((paid / totalFees) * 100)}%</span>
        </div>
        <div className="h-3 rounded-full overflow-hidden" style={{ background: C.bg }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${(paid / totalFees) * 100}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="h-full rounded-full"
            style={{ background: C.green }}
          />
        </div>
        <div className="flex justify-between text-[10px] mt-2" style={{ color: C.textMuted }}>
          <span>Paid: {fmtUGX(paid)}</span>
          <span>Balance: {fmtUGX(outstanding)}</span>
        </div>
        <Button size="sm" className="w-full h-9 text-xs rounded-lg mt-3" style={{ background: C.accent, color: "white" }} onClick={onPayFees}>
          <CreditCardIcon className="h-3.5 w-3.5 mr-1.5" />Pay Now
        </Button>
      </div>

      {/* Fee Breakdown */}
      <PanelCard title="Fee Breakdown" icon={Receipt} iconBg={C.orangeLight}>
        <div className="space-y-2">
          {invoices.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg" style={{ background: C.bg }}>
              <div className="flex items-center gap-2">
                <span className="text-xs" style={{ color: C.text }}>{inv.label}</span>
                <Badge className="text-[9px]" style={{ background: inv.status === "paid" ? C.greenLight : inv.status === "partial" ? C.orangeLight : C.redLight, color: inv.status === "paid" ? C.green : inv.status === "partial" ? C.orange : C.red, border: "none" }}>
                  {inv.status}
                </Badge>
              </div>
              <span className="text-xs font-bold" style={{ color: C.text }}>{fmtUGX(inv.amount)}</span>
            </div>
          ))}
        </div>
      </PanelCard>

      {/* Recent Payments */}
      <PanelCard title="Payment History" icon={History} iconBg={C.greenLight}>
        <div className="space-y-2">
          {payments.map((pay) => (
            <div key={pay.id} className="flex items-center gap-3 p-2.5 rounded-lg" style={{ background: C.bg }}>
              <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: C.greenLight }}>
                <CheckCircle className="h-4 w-4" style={{ color: C.green }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold" style={{ color: C.text }}>{fmtUGX(pay.amount)}</p>
                <p className="text-[10px]" style={{ color: C.textMuted }}>{pay.method} · {pay.date}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-mono" style={{ color: C.textMuted }}>{pay.ref}</p>
              </div>
            </div>
          ))}
        </div>
      </PanelCard>

      {/* Payment Methods */}
      <PanelCard title="Quick Payment" icon={CreditCard} iconBg={C.blueLight}>
        <div className="grid grid-cols-3 gap-2">
          {[
            { method: "MTN MoMo", icon: Phone, color: "#FFCC00" },
            { method: "Airtel Money", icon: Phone, color: "#ED1C24" },
            { method: "Card", icon: CreditCard, color: C.blue },
          ].map((m) => (
            <button key={m.method} className="p-3 rounded-xl text-center transition-all hover:shadow-md" style={{ background: C.bg }}>
              <m.icon className="h-6 w-6 mx-auto" style={{ color: m.color }} />
              <p className="text-[10px] font-semibold mt-1" style={{ color: C.text }}>{m.method}</p>
            </button>
          ))}
        </div>
      </PanelCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// HEALTH TAB — Permissioned data
// ════════════════════════════════════════════════════════════════════════════

export function HealthTab({ studentId }: { studentId: string }) {
  const [activeSection, setActiveSection] = useState<"overview" | "visits" | "vaccinations" | "wellbeing">("overview");

  const sections = ["overview", "visits", "vaccinations", "wellbeing"] as const;

  return (
    <div className="space-y-4">
      {/* Section nav */}
      <div className="flex gap-1.5 overflow-x-auto [scrollbar-width:none]">
        {sections.map((s) => (
          <button
            key={s}
            onClick={() => setActiveSection(s)}
            className="px-4 py-2 rounded-xl text-xs font-semibold capitalize whitespace-nowrap transition-all"
            style={{ background: activeSection === s ? C.pink : C.bg, color: activeSection === s ? "white" : C.textMuted }}
          >
            {s}
          </button>
        ))}
      </div>

      {activeSection === "overview" && (
        <div className="space-y-4">
          {/* Vitals card */}
          <div className="rounded-2xl p-5" style={{ background: `linear-gradient(135deg, ${C.pink} 0%, #be185d 100%)` }}>
            <p className="text-white/70 text-xs uppercase tracking-widest mb-3">Latest Health Record</p>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: "Height", value: "158 cm", icon: Activity },
                { label: "Weight", value: "48 kg", icon: Heart },
                { label: "BMI", value: "19.2", icon: BarChart3 },
                { label: "Blood Group", value: "O+", icon: Droplets },
                { label: "Temperature", value: "36.8°C", icon: Thermometer },
                { label: "Last Check", value: "June 28", icon: Calendar },
              ].map((v) => (
                <div key={v.label} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(255,255,255,0.2)" }}>
                    <v.icon className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-white/60 text-xs">{v.label}</p>
                    <p className="text-white font-bold text-sm">{v.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Alerts */}
          <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: `${C.amber}08`, border: `1px solid ${C.amber}30` }}>
            <AlertTriangle className="w-5 h-5 shrink-0" style={{ color: C.amber }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: C.text }}>Allergy Alert</p>
              <p className="text-xs mt-0.5" style={{ color: C.textMid }}>Peanut allergy — EpiPen available at school clinic</p>
            </div>
          </div>

          {/* Medications */}
          <PanelCard title="Current Medications" icon={Pill} iconBg={C.purpleLight}>
            <div className="space-y-2">
              {[
                { name: "Antihistamine (Cetirizine)", dose: "10mg daily", note: "For seasonal allergies" },
              ].map((med, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: C.bg }}>
                  <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: C.purpleLight }}>
                    <Pill className="w-4 h-4" style={{ color: C.purple }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-semibold" style={{ color: C.text }}>{med.name}</p>
                    <p className="text-[10px]" style={{ color: C.textMuted }}>{med.dose} · {med.note}</p>
                  </div>
                </div>
              ))}
            </div>
          </PanelCard>

          {/* Immunizations summary */}
          <PanelCard title="Immunizations" icon={ShieldCheck} iconBg={C.greenLight}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold" style={{ color: C.green }}>12/12</p>
                <p className="text-xs" style={{ color: C.textMuted }}>Vaccines complete</p>
              </div>
              <div className="h-12 w-12 rounded-full flex items-center justify-center" style={{ background: C.greenLight }}>
                <CheckCircle className="h-6 w-6" style={{ color: C.green }} />
              </div>
            </div>
          </PanelCard>
        </div>
      )}

      {activeSection === "visits" && (
        <PanelCard title="Clinic Visits" icon={Stethoscope} iconBg={C.blueLight}>
          <div className="space-y-3">
            {[
              { date: "Jun 20, 2025", reason: "Headache", nurse: "Nurse Katusabe", note: "Rest advised, recovered fully" },
              { date: "Mar 15, 2025", reason: "Sports injury", nurse: "Nurse Katusabe", note: "Minor bruise - ice pack applied" },
            ].map((v, i) => (
              <div key={i} className="p-3 rounded-xl border" style={{ background: C.bg, borderColor: C.border }}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold" style={{ color: C.text }}>{v.date}</p>
                  <span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: C.greenLight, color: C.green }}>Resolved</span>
                </div>
                <p className="text-xs font-semibold" style={{ color: C.text }}>{v.reason}</p>
                <p className="text-[10px] mt-1" style={{ color: C.textMuted }}>{v.nurse} · {v.note}</p>
              </div>
            ))}
          </div>
        </PanelCard>
      )}

      {activeSection === "vaccinations" && (
        <PanelCard title="Vaccination Record" icon={Shield} iconBg={C.greenLight}>
          <div className="space-y-3">
            {[
              { name: "BCG", date: "Birth", status: "done" },
              { name: "DTP (5 doses)", date: "2010-2014", status: "done" },
              { name: "Measles", date: "May 2015", status: "done" },
              { name: "COVID-19 Booster", date: "Jan 2024", status: "done" },
              { name: "Annual Flu Vaccine", date: "Jun 2025", status: "due" },
            ].map((v, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: C.bg }}>
                <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: v.status === "done" ? C.greenLight : C.orangeLight }}>
                  <ShieldCheck className="w-4 h-4" style={{ color: v.status === "done" ? C.green : C.orange }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold" style={{ color: C.text }}>{v.name}</p>
                  <p className="text-[10px]" style={{ color: C.textMuted }}>{v.date}</p>
                </div>
                <span className="px-2 py-0.5 rounded text-[10px] font-semibold capitalize" style={{ background: v.status === "done" ? C.greenLight : C.orangeLight, color: v.status === "done" ? C.green : C.orange }}>
                  {v.status}
                </span>
              </div>
            ))}
          </div>
        </PanelCard>
      )}

      {activeSection === "wellbeing" && (
        <div className="space-y-4">
          <div className="rounded-2xl p-5" style={{ background: `linear-gradient(135deg, ${C.purple} 0%, #5b21b6 100%)` }}>
            <p className="text-white font-bold text-lg mb-1">Mental Wellness</p>
            <p className="text-white/70 text-sm mb-4">Weekly mood check-in summary</p>
            <div className="flex items-end justify-between gap-2">
              {["Mon", "Tue", "Wed", "Thu", "Fri"].map((day, i) => {
                const moods = [4, 3, 5, 4, 5];
                return (
                  <div key={day} className="flex-1 flex flex-col items-center gap-1">
                    <div className="text-white text-lg">{["😔", "😐", "🙂", "😊", "😁"][moods[i] - 1]}</div>
                    <div className="w-full rounded-full overflow-hidden" style={{ height: moods[i] * 8, background: "rgba(255,255,255,0.15)" }}>
                      <div className="w-full h-full" style={{ background: "rgba(255,255,255,0.6)" }} />
                    </div>
                    <span className="text-white/70 text-xs">{day}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <PanelCard title="Wellbeing Indicators" icon={Brain} iconBg={C.purpleLight}>
            <div className="space-y-3">
              {[
                { label: "Sleep Quality", value: 75, color: C.purple },
                { label: "Physical Activity", value: 85, color: C.green },
                { label: "Social Connection", value: 90, color: C.blue },
                { label: "Academic Stress", value: 40, color: C.orange },
              ].map((ind) => (
                <div key={ind.label}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span style={{ color: C.textMuted }}>{ind.label}</span>
                    <span className="font-bold" style={{ color: ind.color }}>{ind.value}%</span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: C.bg }}>
                    <motion.div initial={{ width: 0 }} animate={{ width: `${ind.value}%` }} transition={{ duration: 0.7 }} className="h-full rounded-full" style={{ background: ind.color }} />
                  </div>
                </div>
              ))}
            </div>
          </PanelCard>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TRANSPORT TAB — Wialon-style live tracking with actual Kampala roads
// ════════════════════════════════════════════════════════════════════════════

interface RouteStop {
  name: string;
  road: string; // Actual road name
  landmark?: string;
  lat: number;
  lng: number;
  eta: string;
  distanceKm: number;
  status: "passed" | "next" | "upcoming";
}

// Actual Kampala roads and landmarks - Route A from TriSphere Academy
const ROUTE_STOPS: RouteStop[] = [
  {
    name: "TriSphere Academy Gate",
    road: "Gayaza Road",
    landmark: "School Main Entrance",
    lat: 0.3476,
    lng: 32.5825,
    eta: "3:05 PM",
    distanceKm: 0,
    status: "passed"
  },
  {
    name: "Ntinda Trading Centre",
    road: "Ntinda-Nsambya Road",
    landmark: "Ntinda Market Junction",
    lat: 0.3551,
    lng: 32.6012,
    eta: "3:18 PM",
    distanceKm: 2.1,
    status: "passed"
  },
  {
    name: "Kisaasi Roundabout",
    road: "Kisaasi Road",
    landmark: "Kisaasi Market",
    lat: 0.3585,
    lng: 32.6078,
    eta: "3:28 PM",
    distanceKm: 3.2,
    status: "next"
  },
  {
    name: "Bukoto Junction",
    road: "Bukoto-Kisojo Road",
    landmark: "Shell Bukoto",
    lat: 0.3420,
    lng: 32.6015,
    eta: "3:42 PM",
    distanceKm: 5.1,
    status: "upcoming"
  },
  {
    name: "Kololo Airstrip Roundabout",
    road: "Jinja Road",
    landmark: "Kololo Ceremonial Grounds",
    lat: 0.3201,
    lng: 32.5954,
    eta: "3:55 PM",
    distanceKm: 7.3,
    status: "upcoming"
  },
  {
    name: "Nakasero Market",
    road: "Nakasero Road",
    landmark: "Clock Tower",
    lat: 0.3141,
    lng: 32.5841,
    eta: "4:08 PM",
    distanceKm: 9.0,
    status: "upcoming"
  },
];

// Road segments with actual Kampala road names and geometry
interface RoadSegment {
  fromStop: number;
  toStop: number;
  roadName: string;
  waypoints: { lat: number; lng: number }[];
  speedLimit: number; // km/h
  trafficFactor: number; // 1 = normal, < 1 = congestion
}

const ROAD_SEGMENTS: RoadSegment[] = [
  {
    fromStop: 0,
    toStop: 1,
    roadName: "Gayaza Road → Ntinda-Nsambya Road",
    speedLimit: 50,
    trafficFactor: 0.85,
    waypoints: [
      { lat: 0.3476, lng: 32.5825 },
      { lat: 0.3492, lng: 32.5870 },
      { lat: 0.3510, lng: 32.5915 },
      { lat: 0.3535, lng: 32.5968 },
      { lat: 0.3551, lng: 32.6012 },
    ]
  },
  {
    fromStop: 1,
    toStop: 2,
    roadName: "Ntinda-Kisaasi Road",
    speedLimit: 45,
    trafficFactor: 0.9,
    waypoints: [
      { lat: 0.3551, lng: 32.6012 },
      { lat: 0.3568, lng: 32.6045 },
      { lat: 0.3580, lng: 32.6068 },
      { lat: 0.3585, lng: 32.6078 },
    ]
  },
  {
    fromStop: 2,
    toStop: 3,
    roadName: "Kisaasi Road → Bukoto Street",
    speedLimit: 40,
    trafficFactor: 0.7, // Congested area
    waypoints: [
      { lat: 0.3585, lng: 32.6078 },
      { lat: 0.3550, lng: 32.6072 },
      { lat: 0.3500, lng: 32.6055 },
      { lat: 0.3455, lng: 32.6035 },
      { lat: 0.3420, lng: 32.6015 },
    ]
  },
  {
    fromStop: 3,
    toStop: 4,
    roadName: "Bukoto Road → Jinja Road",
    speedLimit: 60,
    trafficFactor: 0.95,
    waypoints: [
      { lat: 0.3420, lng: 32.6015 },
      { lat: 0.3380, lng: 32.6002 },
      { lat: 0.3320, lng: 32.5985 },
      { lat: 0.3260, lng: 32.5970 },
      { lat: 0.3201, lng: 32.5954 },
    ]
  },
  {
    fromStop: 4,
    toStop: 5,
    roadName: "Jinja Road → Nakasero Road",
    speedLimit: 50,
    trafficFactor: 0.6, // Heavy city center traffic
    waypoints: [
      { lat: 0.3201, lng: 32.5954 },
      { lat: 0.3178, lng: 32.5910 },
      { lat: 0.3160, lng: 32.5880 },
      { lat: 0.3141, lng: 32.5841 },
    ]
  },
];

// Generate all waypoints from road segments
function generateAllWaypoints(): { lat: number; lng: number; segmentIdx: number }[] {
  const all: { lat: number; lng: number; segmentIdx: number }[] = [];
  ROAD_SEGMENTS.forEach((seg, segIdx) => {
    seg.waypoints.forEach(wp => {
      all.push({ lat: wp.lat, lng: wp.lng, segmentIdx: segIdx });
    });
  });
  return all;
}

const ALL_WAYPOINTS = generateAllWaypoints();
const TOTAL_WAYPOINTS = ALL_WAYPOINTS.length;

function calculateSegmentDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000; // Earth radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

function calculateBearing(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const lat1Rad = lat1 * Math.PI / 180;
  const lat2Rad = lat2 * Math.PI / 180;
  const y = Math.sin(dLng) * Math.cos(lat2Rad);
  const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) -
            Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);
  let bearing = Math.atan2(y, x) * 180 / Math.PI;
  return (bearing + 360) % 360;
}

// Calculate total route distance
const TOTAL_DISTANCE_METERS = (() => {
  let total = 0;
  for (let i = 1; i < ALL_WAYPOINTS.length; i++) {
    total += calculateSegmentDistance(
      ALL_WAYPOINTS[i-1].lat, ALL_WAYPOINTS[i-1].lng,
      ALL_WAYPOINTS[i].lat, ALL_WAYPOINTS[i].lng
    );
  }
  return total;
})();

// Initial waypoint index - start near Kisaasi (after stop 2)
const INITIAL_WAYPOINT_INDEX = 8;

const INITIAL_TELEMETRY = {
  lat: ALL_WAYPOINTS[INITIAL_WAYPOINT_INDEX]?.lat || 0.3585,
  lng: ALL_WAYPOINTS[INITIAL_WAYPOINT_INDEX]?.lng || 32.6078,
  heading: 180,
  speed: 35,
  fuel: 82,
  routeProgress: INITIAL_WAYPOINT_INDEX / TOTAL_WAYPOINTS,
  currentWaypointIndex: INITIAL_WAYPOINT_INDEX,
  currentSegmentIdx: ALL_WAYPOINTS[INITIAL_WAYPOINT_INDEX]?.segmentIdx || 2,
  distanceTraveled: 3200,
  totalDistance: TOTAL_DISTANCE_METERS,
  lastUpdate: new Date(),
};

// Determine stop status based on current position
function getStopStatuses(currentWaypointIndex: number): RouteStop[] {
  return ROUTE_STOPS.map((stop, idx) => {
    // Find closest waypoint to this stop
    let closestIdx = 0;
    let minDist = Infinity;
    ALL_WAYPOINTS.forEach((wp, i) => {
      const dist = Math.sqrt(Math.pow(wp.lat - stop.lat, 2) + Math.pow(wp.lng - stop.lng, 2));
      if (dist < minDist) { minDist = dist; closestIdx = i; }
    });

    if (currentWaypointIndex >= closestIdx + 3) {
      return { ...stop, status: "passed" as const };
    } else if (currentWaypointIndex >= closestIdx - 3 && currentWaypointIndex < closestIdx + 3) {
      return { ...stop, status: "next" as const };
    } else {
      return { ...stop, status: "upcoming" as const };
    }
  });
}

// Get current road segment name
function getCurrentRoadName(segmentIdx: number): string {
  return ROAD_SEGMENTS[segmentIdx]?.roadName || "En Route";
}

export function TransportTab() {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const busMarkerRef = useRef<any>(null);
  const routeLineRef = useRef<any>(null);
  const passedLineRef = useRef<any>(null);
  const stopMarkersRef = useRef<any[]>([]);
  const [mapError, setMapError] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [tracking, setTracking] = useState(true);
  const [showTelemetryPanel, setShowTelemetryPanel] = useState(true);
  const [busModalOpen, setBusModalOpen] = useState(false);

  // Telemetry state
  const [lat, setLat] = useState(INITIAL_TELEMETRY.lat);
  const [lng, setLng] = useState(INITIAL_TELEMETRY.lng);
  const [heading, setHeading] = useState(INITIAL_TELEMETRY.heading);
  const [speed, setSpeed] = useState(INITIAL_TELEMETRY.speed);
  const [fuel, setFuel] = useState(INITIAL_TELEMETRY.fuel);
  const [currentWaypointIndex, setCurrentWaypointIndex] = useState(INITIAL_TELEMETRY.currentWaypointIndex);
  const [distanceTraveled, setDistanceTraveled] = useState(INITIAL_TELEMETRY.distanceTraveled);
  const [currentStops, setCurrentStops] = useState<RouteStop[]>(ROUTE_STOPS);

  // Get current road name
  const currentRoad = getCurrentRoadName(ALL_WAYPOINTS[currentWaypointIndex]?.segmentIdx ?? 0);
  const nextStop = currentStops.find((s) => s.status === "next") || currentStops[0];

  // Live position simulation - follows actual Kampala roads
  useEffect(() => {
    if (!tracking) return;

    const interval = setInterval(() => {
      setCurrentWaypointIndex((prevIdx) => {
        const nextIdx = Math.min(prevIdx + 1, ALL_WAYPOINTS.length - 1);
        const currentWp = ALL_WAYPOINTS[prevIdx];
        const nextWp = ALL_WAYPOINTS[nextIdx];

        if (!currentWp || !nextWp || prevIdx >= ALL_WAYPOINTS.length - 1) {
          return prevIdx;
        }

        // Calculate heading
        const newHeading = calculateBearing(currentWp.lat, currentWp.lng, nextWp.lat, nextWp.lng);
        setHeading(newHeading);

        // Get speed limit for current segment
        const segment = ROAD_SEGMENTS[currentWp.segmentIdx];
        const baseSpeed = segment?.speedLimit || 40;
        const trafficFactor = segment?.trafficFactor || 0.8;
        const actualSpeed = Math.round(baseSpeed * trafficFactor + (Math.random() - 0.5) * 8);
        setSpeed(Math.max(15, Math.min(60, actualSpeed)));

        // Update position
        setLat(nextWp.lat);
        setLng(nextWp.lng);

        // Update distance
        const segmentDist = calculateSegmentDistance(currentWp.lat, currentWp.lng, nextWp.lat, nextWp.lng);
        setDistanceTraveled(prev => prev + segmentDist);

        // Decrease fuel
        setFuel(prev => Math.max(50, prev - 0.03));

        return nextIdx;
      });
    }, 2500);

    return () => clearInterval(interval);
  }, [tracking]);

  // Update stop statuses based on position
  useEffect(() => {
    setCurrentStops(getStopStatuses(currentWaypointIndex));
  }, [currentWaypointIndex]);

  // Map initialization
  useEffect(() => {
    loadGoogleMaps()
      .then((google) => {
        if (!mapRef.current) return;

        // Calculate bounds for entire route
        const bounds = new (google as any).maps.LatLngBounds();
        ALL_WAYPOINTS.forEach(wp => bounds.extend({ lat: wp.lat, lng: wp.lng }));

        const map = new (google as any).maps.Map(mapRef.current, {
          center: { lat: INITIAL_TELEMETRY.lat, lng: INITIAL_TELEMETRY.lng },
          zoom: 14,
          disableDefaultUI: true,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          styles: [
            { featureType: "all", elementType: "labels.text.fill", stylers: [{ color: "#6b7280" }] },
            { featureType: "all", elementType: "labels.text.stroke", stylers: [{ visibility: "off" }] },
            { featureType: "administrative", elementType: "geometry", stylers: [{ visibility: "off" }] },
            { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#1a1f2e" }] },
            { featureType: "poi", stylers: [{ visibility: "off" }] },
            { featureType: "road", elementType: "geometry", stylers: [{ color: "#2d3748" }] },
            { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#374151" }] },
            { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#4a5568" }] },
            { featureType: "transit", stylers: [{ visibility: "off" }] },
            { featureType: "water", elementType: "geometry", stylers: [{ color: "#0d1321" }] },
          ],
        });
        map.fitBounds(bounds, 60);
        mapInstanceRef.current = map;

        // Draw road segments with traffic coloring
        ROAD_SEGMENTS.forEach((seg, segIdx) => {
          const path = seg.waypoints.map(wp => ({ lat: wp.lat, lng: wp.lng }));
          const trafficColor = seg.trafficFactor < 0.7 ? "#B33A1A" : seg.trafficFactor < 0.85 ? "#f59e0b" : "#4a5568";
          new (google as any).maps.Polyline({
            path,
            strokeColor: trafficColor,
            strokeOpacity: 0.6,
            strokeWeight: 5,
            map,
            zIndex: 1,
          });
        });

        // Passed route polyline (golden, animated)
        const passedPath = ALL_WAYPOINTS.slice(0, currentWaypointIndex + 1).map(wp => ({ lat: wp.lat, lng: wp.lng }));
        const passedLine = new (google as any).maps.Polyline({
          path: passedPath,
          strokeColor: "#C4A882",
          strokeOpacity: 1,
          strokeWeight: 5,
          icons: [{ icon: { path: (google as any).maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2, strokeColor: "#C4A882" }, repeat: "60px" }],
          map,
          zIndex: 2,
        });
        passedLineRef.current = passedLine;

        // Stop markers with road labels
        ROUTE_STOPS.forEach((stop, idx) => {
          const m = new (google as any).maps.Marker({
            position: { lat: stop.lat, lng: stop.lng },
            map,
            title: `${stop.name} (${stop.road})`,
            icon: {
              path: (google as any).maps.SymbolPath.CIRCLE,
              scale: stop.status === "next" ? 10 : 8,
              fillColor: stop.status === "passed" ? "#4a5568" : stop.status === "next" ? "#C4A882" : "#7B1416",
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 2,
            },
          });
          stopMarkersRef.current.push(m);
        });

        // Bus marker with click handler
        const busIcon = {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="48" height="48">
              <g transform="rotate(${Math.round(heading)} 24 24)">
                <circle cx="24" cy="24" r="20" fill="#C4A882" stroke="#ffffff" stroke-width="3"/>
                <path d="M16 18 L32 18 L34 22 L34 30 L32 32 L28 32 L28 34 L26 34 L26 32 L22 32 L22 34 L20 34 L20 32 L16 32 L14 30 L14 22 Z" fill="#7B1416"/>
                <rect x="18" y="20" width="6" height="4" rx="1" fill="#87CEEB"/>
                <rect x="26" y="20" width="4" height="4" rx="1" fill="#87CEEB"/>
                <circle cx="18" cy="30" r="2" fill="#333"/>
                <circle cx="30" cy="30" r="2" fill="#333"/>
              </g>
            </svg>`)}`,
          scaledSize: new (google as any).maps.Size(48, 48),
          anchor: new (google as any).maps.Point(24, 24),
        };

        const busMarker = new (google as any).maps.Marker({
          position: { lat: INITIAL_TELEMETRY.lat, lng: INITIAL_TELEMETRY.lng },
          map,
          title: "School Bus — Route A (Click for details)",
          icon: busIcon,
          zIndex: 999,
        });

        // Click handler for bus modal
        busMarker.addListener("click", () => setBusModalOpen(true));
        busMarkerRef.current = busMarker;

        setMapLoaded(true);
      })
      .catch(() => setMapError(true));

    return () => {
      stopMarkersRef.current.forEach((m) => m.setMap(null));
      stopMarkersRef.current = [];
      if (busMarkerRef.current) busMarkerRef.current.setMap(null);
      if (routeLineRef.current) routeLineRef.current.setMap(null);
      if (passedLineRef.current) passedLineRef.current.setMap(null);
    };
  }, []);

  // Update bus marker position
  useEffect(() => {
    if (!mapInstanceRef.current || !busMarkerRef.current || !window.google) return;
    const google = window.google;

    busMarkerRef.current.setPosition({ lat, lng });

    const busIcon = {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="48" height="48">
          <g transform="rotate(${Math.round(heading)} 24 24)">
            <circle cx="24" cy="24" r="20" fill="#C4A882" stroke="#ffffff" stroke-width="3"/>
            <path d="M16 18 L32 18 L34 22 L34 30 L32 32 L28 32 L28 34 L26 34 L26 32 L22 32 L22 34 L20 34 L20 32 L16 32 L14 30 L14 22 Z" fill="#7B1416"/>
            <rect x="18" y="20" width="6" height="4" rx="1" fill="#87CEEB"/>
            <rect x="26" y="20" width="4" height="4" rx="1" fill="#87CEEB"/>
            <circle cx="18" cy="30" r="2" fill="#333"/>
            <circle cx="30" cy="30" r="2" fill="#333"/>
          </g>
        </svg>`)}`,
      scaledSize: new (google as any).maps.Size(48, 48),
      anchor: new (google as any).maps.Point(24, 24),
    };
    busMarkerRef.current.setIcon(busIcon);

    // Update passed route line
    if (passedLineRef.current && currentWaypointIndex > 0) {
      const passedPath = ALL_WAYPOINTS.slice(0, currentWaypointIndex + 1).map(wp => ({ lat: wp.lat, lng: wp.lng }));
      passedLineRef.current.setPath(passedPath);
    }

    mapInstanceRef.current.panTo({ lat, lng });
  }, [lat, lng, heading, currentWaypointIndex]);

  // Update stop markers based on status
  useEffect(() => {
    if (!window.google || stopMarkersRef.current.length === 0) return;
    const google = window.google;

    currentStops.forEach((stop, i) => {
      const marker = stopMarkersRef.current[i];
      if (!marker) return;

      let color = "#7B1416";
      let scale = 8;
      if (stop.status === "passed") { color = "#4a5568"; scale = 6; }
      else if (stop.status === "next") { color = "#C4A882"; scale = 12; }

      marker.setIcon({
        path: (google as any).maps.SymbolPath.CIRCLE,
        scale,
        fillColor: color,
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 2,
      });
    });
  }, [currentStops]);

  const progressPercent = Math.round((currentWaypointIndex / TOTAL_WAYPOINTS) * 100);
  const distanceKm = (distanceTraveled / 1000).toFixed(1);
  const totalDistanceKm = (TOTAL_DISTANCE_METERS / 1000).toFixed(1);

  return (
    <div className="space-y-4">
      {/* Wialon-style header */}
      <div className="rounded-2xl overflow-hidden" style={{ background: C.forest }}>
        <div className="px-4 py-3 flex items-center justify-between border-b" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setBusModalOpen(true)}
              className="w-10 h-10 rounded-xl flex items-center justify-center transition hover:scale-105"
              style={{ background: "rgba(255,255,255,0.15)" }}
            >
              <RadioIcon className="w-5 h-5 text-white" />
            </button>
            <div>
              <p className="text-white font-bold">Fleet Tracking — Route A</p>
              <p className="text-xs text-white/60">{currentRoad}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${tracking ? "bg-green-400 animate-pulse" : "bg-gray-400"}`} />
            <span className="text-xs font-semibold text-white/80">{tracking ? "LIVE" : "PAUSED"}</span>
            <button onClick={() => setTracking((t) => !t)} className="ml-2 w-8 h-8 rounded-lg flex items-center justify-center transition" style={{ background: "rgba(255,255,255,0.15)" }}>
              {tracking ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white" />}
            </button>
          </div>
        </div>

        {/* Map container */}
        <div className="relative" style={{ height: 280 }}>
          <div ref={mapRef} className="w-full h-full" />

          {/* Telemetry Panel */}
          <AnimatePresence>
            {showTelemetryPanel && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="absolute top-3 left-3 rounded-xl overflow-hidden shadow-2xl"
                style={{ background: "rgba(31,49,58,0.95)", border: "1px solid rgba(255,255,255,0.1)", width: 160 }}
              >
                <div className="px-3 py-2 border-b" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                  <p className="text-xs font-bold text-white/60 uppercase tracking-wider">Telemetry</p>
                </div>
                <div className="p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <GaugeIcon className="w-3 h-3 text-cyan-400" />
                      <span className="text-xs text-white/60">Speed</span>
                    </div>
                    <span className="text-sm font-bold text-white">{Math.round(speed)} <span className="text-xs text-white/60">km/h</span></span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Navigation className="w-3 h-3 text-yellow-400" style={{ transform: `rotate(${heading}deg)` }} />
                      <span className="text-xs text-white/60">Heading</span>
                    </div>
                    <span className="text-sm font-bold text-white">{Math.round(heading)}°</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FuelIcon className="w-3 h-3 text-orange-400" />
                      <span className="text-xs text-white/60">Fuel</span>
                    </div>
                    <span className="text-sm font-bold text-white">{Math.round(fuel)}%</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <button onClick={() => setShowTelemetryPanel((p) => !p)} className="absolute top-3 right-3 w-8 h-8 rounded-lg flex items-center justify-center transition" style={{ background: "rgba(31,49,58,0.95)", border: "1px solid rgba(255,255,255,0.1)" }}>
            <Eye className="w-4 h-4 text-white" />
          </button>

          {!mapLoaded && !mapError && (
            <div className="absolute inset-0 flex items-center justify-center" style={{ background: "#1a1f2e" }}>
              <Loader2 className="w-8 h-8 text-white/30 animate-spin" />
            </div>
          )}
          {mapError && (
            <div className="absolute inset-0 flex items-center justify-center" style={{ background: "#1a1f2e" }}>
              <div className="text-center">
                <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-yellow-500" />
                <p className="text-sm font-medium text-white/80">Map unavailable</p>
              </div>
            </div>
          )}
        </div>

        {/* Bottom info bar with route progress */}
        <div className="px-4 py-3 border-t" style={{ borderColor: "rgba(255,255,255,0.1)", background: "rgba(0,0,0,0.2)" }}>
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-xs text-white/50">Next Stop</p>
              <p className="font-bold text-white">{nextStop.name}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-white/50">ETA</p>
              <p className="font-bold text-yellow-400">{nextStop.eta}</p>
            </div>
          </div>
          {/* Route progress bar */}
          <div className="mt-3">
            <div className="flex justify-between text-xs text-white/50 mb-1">
              <span>Route Progress</span>
              <span>{progressPercent}% · {distanceKm}km / {totalDistanceKm}km</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.1)" }}>
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progressPercent}%`, background: "linear-gradient(90deg, #C4A882 0%, #C4A882 100%)" }} />
            </div>
          </div>
        </div>
      </div>

      {/* Route stops timeline */}
      <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${C.border}` }}>
        <div className="px-4 py-3 border-b flex items-center justify-between" style={{ background: C.card, borderColor: C.border }}>
          <p className="font-bold text-sm" style={{ color: C.text }}>Route A — Stop Progress</p>
          <span className="px-2 py-1 rounded-lg text-xs font-semibold" style={{ background: C.greenLight, color: C.green }}>ON ROUTE</span>
        </div>
        <div style={{ background: C.card }}>
          {currentStops.map((stop, i) => {
            const color = stop.status === "passed" ? C.textLight : stop.status === "next" ? C.mustard : C.textMuted;
            const Icon = stop.status === "passed" ? CheckCircle : stop.status === "next" ? RadioIcon : Clock;
            return (
              <div key={i} className="flex items-center gap-4 px-4 py-3 border-b last:border-0" style={{ borderColor: C.border }}>
                <div className="relative flex flex-col items-center">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center ${stop.status === "next" ? "ring-4 ring-yellow-100" : ""}`} style={{ background: stop.status === "passed" ? C.textLight + "20" : stop.status === "next" ? C.mustard : C.bg, border: `2px solid ${color}` }}>
                    <Icon className={`w-4 h-4 ${stop.status === "next" ? "animate-pulse" : ""}`} style={{ color }} />
                  </div>
                  {i < currentStops.length - 1 && (
                    <div className="w-0.5 h-6 mt-1 absolute top-9" style={{ background: i < currentStops.findIndex((s) => s.status === "next") ? C.mustard : C.border }} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${stop.status === "next" ? "" : "font-normal"}`} style={{ color: stop.status === "passed" ? C.textLight : C.text }}>
                    {stop.name}
                    {stop.status === "next" && (
                      <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: C.mustard + "20", color: C.mustard }}>NEXT</span>
                    )}
                  </p>
                  <p className="text-[10px] mt-0.5" style={{ color: C.textMuted }}>{stop.road}{stop.landmark ? ` · ${stop.landmark}` : ""}</p>
                </div>
                <span className="text-sm font-semibold shrink-0" style={{ color }}>{stop.eta}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Driver & vehicle info */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl p-4" style={{ background: C.card, border: `1px solid ${C.border}` }}>
          <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: C.textLight }}>Driver</p>
          <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3 font-bold text-white text-lg" style={{ background: C.forest }}>R</div>
          <p className="font-bold" style={{ color: C.text }}>Robert Mugisha</p>
          <p className="text-xs mt-0.5" style={{ color: C.textMuted }}>6 years exp · Rating 4.8</p>
          <div className="flex items-center gap-3 mt-3">
            <a href="tel:+256782111222" className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: C.forest }}>
              <Phone className="w-3.5 h-3.5" /> Call
            </a>
            <a href="mailto:driver@trischool.com" className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: C.accent }}>
              <Mail className="w-3.5 h-3.5" /> Message
            </a>
          </div>
        </div>
        <div className="rounded-2xl p-4" style={{ background: C.card, border: `1px solid ${C.border}` }}>
          <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: C.textLight }}>Vehicle</p>
          <p className="font-bold text-2xl" style={{ color: C.forest }}>UG 0042</p>
          <p className="text-xs font-semibold mt-0.5" style={{ color: C.textMuted }}>TCA · Toyota Coaster</p>
          <div className="mt-3 space-y-1.5 text-xs" style={{ color: C.textMuted }}>
            <p className="flex items-center justify-between">
              <span>Capacity</span>
              <span className="font-semibold" style={{ color: C.text }}>28 seats</span>
            </p>
            <p className="flex items-center justify-between">
              <span>Last Service</span>
              <span className="font-semibold" style={{ color: C.text }}>Jun 2026</span>
            </p>
          </div>
          <div className="flex items-center gap-1.5 mt-3">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs font-semibold" style={{ color: C.green }}>Active on Route</span>
          </div>
        </div>
      </div>

      {/* Bus Detail Modal */}
      <Dialog open={busModalOpen} onOpenChange={setBusModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: C.mustard }}>
                  <Bus className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold" style={{ color: C.text }}>School Bus — Route A</h2>
                  <p className="text-sm" style={{ color: C.textMuted }}>UG 0042 · Toyota Coaster</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${tracking ? "bg-green-500 animate-pulse" : "bg-amber-500"}`} />
                <span className="text-xs font-semibold" style={{ color: tracking ? C.green : C.amber }}>{tracking ? "ON ROUTE" : "IDLE"}</span>
              </div>
            </div>

            {/* Current Location */}
            <div className="rounded-xl p-4 mb-4" style={{ background: C.bg }}>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4" style={{ color: C.mustard }} />
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: C.textMuted }}>Current Location</span>
              </div>
              <p className="font-bold text-lg" style={{ color: C.text }}>{currentRoad}</p>
              <p className="text-sm" style={{ color: C.textMuted }}>
                Near {nextStop.name} · {nextStop.road}
              </p>
            </div>

            {/* Telemetry Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div className="rounded-xl p-3 text-center" style={{ background: C.bg }}>
                <GaugeIcon className="w-5 h-5 mx-auto mb-1" style={{ color: C.accent }} />
                <p className="text-2xl font-bold" style={{ color: C.text }}>{Math.round(speed)}</p>
                <p className="text-xs" style={{ color: C.textMuted }}>km/h</p>
              </div>
              <div className="rounded-xl p-3 text-center" style={{ background: C.bg }}>
                <Navigation className="w-5 h-5 mx-auto mb-1" style={{ color: C.amber, transform: `rotate(${heading}deg)` }} />
                <p className="text-2xl font-bold" style={{ color: C.text }}>{Math.round(heading)}°</p>
                <p className="text-xs" style={{ color: C.textMuted }}>Heading</p>
              </div>
              <div className="rounded-xl p-3 text-center" style={{ background: C.bg }}>
                <FuelIcon className="w-5 h-5 mx-auto mb-1" style={{ color: C.orange }} />
                <p className="text-2xl font-bold" style={{ color: fuel > 70 ? C.green : fuel > 40 ? C.amber : C.red }}>{Math.round(fuel)}%</p>
                <p className="text-xs" style={{ color: C.textMuted }}>Fuel</p>
              </div>
              <div className="rounded-xl p-3 text-center" style={{ background: C.bg }}>
                <Activity className="w-5 h-5 mx-auto mb-1" style={{ color: C.teal }} />
                <p className="text-2xl font-bold" style={{ color: C.text }}>{progressPercent}%</p>
                <p className="text-xs" style={{ color: C.textMuted }}>Progress</p>
              </div>
            </div>

            {/* Route Progress */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span style={{ color: C.textMuted }}>Distance Traveled</span>
                <span className="font-bold" style={{ color: C.text }}>{distanceKm}km / {totalDistanceKm}km</span>
              </div>
              <div className="h-3 rounded-full overflow-hidden" style={{ background: C.bg }}>
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progressPercent}%`, background: `linear-gradient(90deg, ${C.mustard} 0%, ${C.green} 100%)` }} />
              </div>
            </div>

            {/* Next Stop Info */}
            <div className="rounded-xl p-4 mb-4" style={{ background: C.mustard + "15", border: `1px solid ${C.mustard}30` }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: C.mustard }}>Next Stop</p>
                  <p className="font-bold" style={{ color: C.text }}>{nextStop.name}</p>
                  <p className="text-xs" style={{ color: C.textMuted }}>{nextStop.road}{nextStop.landmark ? ` · ${nextStop.landmark}` : ""}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold" style={{ color: C.mustard }}>{nextStop.eta}</p>
                  <p className="text-xs" style={{ color: C.textMuted }}>ETA</p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2">
              <Button
                className="flex-1"
                style={{ background: C.forest }}
                onClick={() => setBusModalOpen(false)}
              >
                <MapPin className="w-4 h-4 mr-2" />
                Track on Map
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setBusModalOpen(false)}
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Driver
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MESSAGES TAB — Structured inbox with category-based navigation
// ════════════════════════════════════════════════════════════════════════════

interface MessageThread {
  id: string;
  subject: string;
  sender: string;
  senderRole: string;
  category: "teacher" | "office" | "billing" | "transport" | "health" | "announcement";
  preview: string;
  timestamp: Date;
  read: boolean;
  pinned: boolean;
  hasAttachment: boolean;
  actionRequired?: "payment" | "consent" | "acknowledge" | "reply" | null;
  studentName?: string;
  schoolName?: string;
}

const MESSAGE_CATEGORIES = [
  { key: "all", label: "All", icon: Inbox },
  { key: "teacher", label: "Teachers", icon: GraduationCap, color: C.blue },
  { key: "office", label: "School Office", icon: Building2, color: C.teal },
  { key: "billing", label: "Billing", icon: CreditCard, color: C.amber },
  { key: "transport", label: "Transport", icon: Bus, color: C.orange },
  { key: "health", label: "Health", icon: Heart, color: C.pink },
  { key: "announcement", label: "Announcements", icon: Bell, color: C.purple },
];

const DEMO_MESSAGES: MessageThread[] = [
  {
    id: "1", subject: "Term 3 Assessment Schedule", sender: "Mr. Okello James", senderRole: "Class Teacher - Primary 6A",
    category: "teacher", preview: "Dear Parent, Please find attached the assessment schedule for Term 3. Mid-term assessments will begin on July 15th...",
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), read: false, pinned: false, hasAttachment: true,
    actionRequired: "acknowledge", studentName: "Emma Nakamya", schoolName: "Greenhill Academy"
  },
  {
    id: "2", subject: "Fee Payment Reminder - Term 3", sender: "Accounts Department", senderRole: "Bursar's Office",
    category: "billing", preview: "Dear Parent, This is a reminder that the Term 3 fee balance of UGX 450,000 is due by July 10th...",
    timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000), read: false, pinned: false, hasAttachment: true,
    actionRequired: "payment", studentName: "Emma Nakamya", schoolName: "Greenhill Academy"
  },
  {
    id: "3", subject: "Sports Day Consent Form", sender: "Mrs. Nabukalu Sarah", senderRole: "Sports Coordinator",
    category: "office", preview: "Dear Parent, The annual Sports Day will be held on July 20th. Please sign and return the attached consent form...",
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000), read: false, pinned: true, hasAttachment: true,
    actionRequired: "consent", studentName: "Emma Nakamya", schoolName: "Greenhill Academy"
  },
  {
    id: "4", subject: "Bus Route Change Notification", sender: "Transport Department", senderRole: "Transport Manager",
    category: "transport", preview: "Dear Parent, Due to roadworks on Kampala Road, Route B will be diverted via Wandegeya starting Monday...",
    timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), read: true, pinned: false, hasAttachment: false,
    actionRequired: null, studentName: "Emma Nakamya", schoolName: "Greenhill Academy"
  },
  {
    id: "5", subject: "Medical Checkup Scheduled", sender: "School Clinic", senderRole: "School Nurse",
    category: "health", preview: "Dear Parent, A routine vision and hearing screening has been scheduled for Emma on July 12th...",
    timestamp: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000), read: true, pinned: false, hasAttachment: false,
    actionRequired: null, studentName: "Emma Nakamya", schoolName: "Greenhill Academy"
  },
  {
    id: "6", subject: "End of Term Concert Invitation", sender: "Headteacher's Office", senderRole: "Headteacher",
    category: "announcement", preview: "Dear Parents, You are cordially invited to the End of Term Concert on August 8th at 2:00 PM...",
    timestamp: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000), read: true, pinned: false, hasAttachment: true,
    actionRequired: null, studentName: undefined, schoolName: "Greenhill Academy"
  },
];

export function MessagesTab({ studentId }: { studentId: string }) {
  const [messages, setMessages] = useState<MessageThread[]>(DEMO_MESSAGES);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMessage, setSelectedMessage] = useState<MessageThread | null>(null);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyTo, setReplyTo] = useState<MessageThread | null>(null);

  const filteredMessages = useMemo(() => {
    return messages.filter(m => {
      const matchesCategory = selectedCategory === "all" || m.category === selectedCategory;
      const matchesSearch = !searchQuery ||
        m.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.sender.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.preview.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRead = !showUnreadOnly || !m.read;
      return matchesCategory && matchesSearch && matchesRead;
    }).sort((a, b) => {
      if (a.pinned !== b.pinned) return b.pinned ? 1 : -1;
      return b.timestamp.getTime() - a.timestamp.getTime();
    });
  }, [messages, selectedCategory, searchQuery, showUnreadOnly]);

  const unreadCount = messages.filter(m => !m.read).length;
  const actionRequiredCount = messages.filter(m => m.actionRequired).length;

  const handleMarkRead = (msgId: string) => {
    setMessages(prev => prev.map(m => m.id === msgId ? { ...m, read: true } : m));
    toast.success("Message marked as read");
  };

  const handlePin = (msgId: string) => {
    setMessages(prev => prev.map(m => m.id === msgId ? { ...m, pinned: !m.pinned } : m));
    toast.success("Pin toggled");
  };

  const handleAction = (msg: MessageThread) => {
    switch (msg.actionRequired) {
      case "payment":
        toast.info("Opening payment portal...");
        break;
      case "consent":
        toast.info("Opening consent form...");
        break;
      case "acknowledge":
        handleMarkRead(msg.id);
        toast.success("Acknowledged");
        break;
      case "reply":
        setReplyTo(msg);
        setComposeOpen(true);
        break;
    }
  };

  const timeAgo = (date: Date) => {
    const diff = Date.now() - date.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString("en-UG", { month: "short", day: "numeric" });
  };

  const getCategoryColor = (category: string) => {
    const cat = MESSAGE_CATEGORIES.find(c => c.key === category);
    return cat?.color || C.textMuted;
  };

  return (
    <div className="space-y-4">
      {/* Header with stats */}
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-xl p-3 text-center" style={{ background: C.card, border: `1px solid ${C.border}` }}>
          <p className="text-2xl font-bold" style={{ color: C.blue }}>{unreadCount}</p>
          <p className="text-xs" style={{ color: C.textMuted }}>Unread</p>
        </div>
        <div className="rounded-xl p-3 text-center" style={{ background: C.card, border: `1px solid ${C.border}` }}>
          <p className="text-2xl font-bold" style={{ color: C.amber }}>{actionRequiredCount}</p>
          <p className="text-xs" style={{ color: C.textMuted }}>Action Required</p>
        </div>
        <div className="rounded-xl p-3 text-center" style={{ background: C.card, border: `1px solid ${C.border}` }}>
          <p className="text-2xl font-bold" style={{ color: C.green }}>{messages.filter(m => m.pinned).length}</p>
          <p className="text-xs" style={{ color: C.textMuted }}>Pinned</p>
        </div>
      </div>

      {/* Search and compose */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: C.textMuted }} />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search messages..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm"
            style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text }}
          />
        </div>
        <button
          onClick={() => { setReplyTo(null); setComposeOpen(true); }}
          className="px-4 rounded-xl flex items-center gap-2 text-white text-sm font-medium"
          style={{ background: C.forest }}
        >
          <PenTool className="h-4 w-4" /> Compose
        </button>
        <button
          onClick={() => setShowUnreadOnly(v => !v)}
          className={`px-3 rounded-xl flex items-center gap-1.5 text-sm font-medium transition ${showUnreadOnly ? "text-white" : ""}`}
          style={{ background: showUnreadOnly ? C.blue : C.card, border: `1px solid ${C.border}`, color: showUnreadOnly ? "white" : C.text }}
        >
          <Mail className="h-4 w-4" />
        </button>
      </div>

      {/* Category tabs */}
      <div className="flex gap-1 overflow-x-auto pb-2 [scrollbar-width:none]">
        {MESSAGE_CATEGORIES.map(cat => (
          <button
            key={cat.key}
            onClick={() => setSelectedCategory(cat.key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition ${selectedCategory === cat.key ? "text-white" : ""}`}
            style={{
              background: selectedCategory === cat.key ? (cat.color || C.forest) : C.card,
              border: `1px solid ${selectedCategory === cat.key ? "transparent" : C.border}`,
              color: selectedCategory === cat.key ? "white" : C.textMid,
            }}
          >
            {cat.icon && <cat.icon className="h-3.5 w-3.5" />}
            {cat.label}
            {cat.key !== "all" && (
              <span className="ml-0.5 px-1.5 py-0.5 rounded text-[10px]" style={{ background: selectedCategory === cat.key ? "rgba(255,255,255,0.2)" : C.bg }}>
                {messages.filter(m => m.category === cat.key).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Messages list */}
      <div className="rounded-2xl overflow-hidden" style={{ background: C.card, border: `1px solid ${C.border}` }}>
        {filteredMessages.length === 0 ? (
          <div className="p-8 text-center">
            <Inbox className="h-12 w-12 mx-auto mb-3" style={{ color: C.textMuted }} />
            <p className="font-medium" style={{ color: C.text }}>No messages found</p>
            <p className="text-sm mt-1" style={{ color: C.textMuted }}>Try adjusting your filters</p>
          </div>
        ) : (
          <AnimatePresence>
            {filteredMessages.map((msg, idx) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className={`border-b last:border-0 cursor-pointer hover:bg-gray-50 transition ${!msg.read ? "bg-blue-50/30" : ""}`}
                style={{ borderColor: C.border }}
                onClick={() => { setSelectedMessage(msg); handleMarkRead(msg.id); }}
              >
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div
                      className="h-10 w-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
                      style={{ background: getCategoryColor(msg.category) }}
                    >
                      {msg.sender.split(" ").map(n => n[0]).join("").slice(0, 2)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2">
                          <p className={`font-semibold text-sm truncate ${!msg.read ? "text-gray-900" : ""}`} style={{ color: !msg.read ? C.text : C.textMid }}>
                            {msg.sender}
                          </p>
                          {msg.pinned && <Bookmark className="h-3 w-3" style={{ color: C.amber }} />}
                          {msg.hasAttachment && <Paperclip className="h-3 w-3" style={{ color: C.textMuted }} />}
                        </div>
                        <span className="text-xs shrink-0" style={{ color: C.textMuted }}>{timeAgo(msg.timestamp)}</span>
                      </div>
                      <p className={`text-sm font-medium truncate mb-1 ${!msg.read ? "text-gray-800" : ""}`} style={{ color: !msg.read ? C.text : C.textMid }}>
                        {msg.subject}
                      </p>
                      <p className="text-xs truncate" style={{ color: C.textMuted }}>{msg.preview}</p>

                      {/* Footer */}
                      <div className="flex items-center gap-2 mt-2">
                        {msg.studentName && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: C.bg, color: C.textMuted }}>
                            {msg.studentName}
                          </span>
                        )}
                        <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${getCategoryColor(msg.category)}15`, color: getCategoryColor(msg.category) }}>
                          {MESSAGE_CATEGORIES.find(c => c.key === msg.category)?.label || msg.category}
                        </span>
                        {msg.actionRequired && (
                          <span
                            className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                            style={{
                              background: msg.actionRequired === "payment" ? C.redLight : msg.actionRequired === "consent" ? C.amberLight : C.blueLight,
                              color: msg.actionRequired === "payment" ? C.red : msg.actionRequired === "consent" ? C.amber : C.blue
                            }}
                          >
                            {msg.actionRequired === "payment" ? "Payment Due" : msg.actionRequired === "consent" ? "Consent Required" : "Action Needed"}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Quick action button */}
                    {msg.actionRequired && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleAction(msg); }}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white shrink-0"
                        style={{ background: getCategoryColor(msg.category) }}
                      >
                        {msg.actionRequired === "payment" ? "Pay" : msg.actionRequired === "consent" ? "Approve" : msg.actionRequired === "reply" ? "Reply" : "View"}
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* Message detail modal */}
      <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
        <DialogContent className="max-w-xl">
          {selectedMessage && (
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="h-12 w-12 rounded-full flex items-center justify-center text-white font-bold"
                    style={{ background: getCategoryColor(selectedMessage.category) }}
                  >
                    {selectedMessage.sender.split(" ").map(n => n[0]).join("").slice(0, 2)}
                  </div>
                  <div>
                    <p className="font-bold" style={{ color: C.text }}>{selectedMessage.sender}</p>
                    <p className="text-xs" style={{ color: C.textMuted }}>{selectedMessage.senderRole}</p>
                  </div>
                </div>
                <button onClick={() => handlePin(selectedMessage.id)} className="p-2 rounded-lg hover:bg-gray-100">
                  <Bookmark className={`h-5 w-5 ${selectedMessage.pinned ? "fill-current" : ""}`} style={{ color: selectedMessage.pinned ? C.amber : C.textMuted }} />
                </button>
              </div>

              <h3 className="font-bold text-lg mb-2" style={{ color: C.text }}>{selectedMessage.subject}</h3>
              <p className="text-xs mb-4" style={{ color: C.textMuted }}>
                {selectedMessage.timestamp.toLocaleString("en-UG", {
                  weekday: "long", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit"
                })}
              </p>

              <div className="rounded-xl p-4 mb-4" style={{ background: C.bg }}>
                <p className="text-sm whitespace-pre-wrap" style={{ color: C.text }}>
                  {selectedMessage.preview}
                  {"\n\n"}
                  Your continued support ensures the best educational outcomes for your child. Please do not hesitate to contact us if you have any questions.
                  {"\n\n"}
                  Best regards,
                  {"\n"}{selectedMessage.sender}
                </p>
              </div>

              {selectedMessage.studentName && (
                <div className="flex items-center gap-2 mb-4">
                  <GraduationCap className="h-4 w-4" style={{ color: C.textMuted }} />
                  <span className="text-sm" style={{ color: C.textMuted }}>
                    Regarding: <span style={{ color: C.text }}>{selectedMessage.studentName}</span>
                    {selectedMessage.schoolName && ` — ${selectedMessage.schoolName}`}
                  </span>
                </div>
              )}

              {selectedMessage.hasAttachment && (
                <div className="flex items-center gap-2 p-3 rounded-lg mb-4" style={{ background: C.bg }}>
                  <Paperclip className="h-4 w-4" style={{ color: C.textMuted }} />
                  <span className="text-sm font-medium" style={{ color: C.text }}>Attachment Available</span>
                  <button className="ml-auto text-xs font-semibold px-2 py-1 rounded" style={{ color: C.accent }}>Download</button>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-2">
                {selectedMessage.actionRequired ? (
                  <Button
                    className="flex-1"
                    style={{ background: getCategoryColor(selectedMessage.category) }}
                    onClick={() => handleAction(selectedMessage)}
                  >
                    {selectedMessage.actionRequired === "payment" && <CreditCard className="h-4 w-4 mr-2" />}
                    {selectedMessage.actionRequired === "consent" && <FilePen className="h-4 w-4 mr-2" />}
                    {selectedMessage.actionRequired === "acknowledge" && <Check className="h-4 w-4 mr-2" />}
                    {selectedMessage.actionRequired === "payment" ? "Pay Now" :
                     selectedMessage.actionRequired === "consent" ? "Review & Approve" :
                     selectedMessage.actionRequired === "acknowledge" ? "Acknowledge" : "Take Action"}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => { setReplyTo(selectedMessage); setComposeOpen(true); setSelectedMessage(null); }}
                  >
                    <Reply className="h-4 w-4 mr-2" /> Reply
                  </Button>
                )}
                <Button variant="ghost" onClick={() => setSelectedMessage(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Compose modal */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-lg">
          <div className="p-6">
            <h3 className="font-bold text-lg mb-4" style={{ color: C.text }}>
              {replyTo ? `Reply to ${replyTo.sender}` : "New Message"}
            </h3>
            <div className="space-y-3">
              <input
                placeholder="Subject"
                defaultValue={replyTo ? `Re: ${replyTo.subject}` : ""}
                className="w-full px-4 py-2.5 rounded-xl text-sm"
                style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
              />
              <select
                className="w-full px-4 py-2.5 rounded-xl text-sm"
                style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
              >
                <option value="teacher">Class Teacher</option>
                <option value="office">School Office</option>
                <option value="billing">Billing</option>
                <option value="transport">Transport Department</option>
              </select>
              <textarea
                placeholder="Write your message..."
                rows={6}
                className="w-full px-4 py-2.5 rounded-xl text-sm resize-none"
                style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
              />
              <div className="flex gap-2 pt-2">
                <Button className="flex-1" style={{ background: C.forest }} onClick={() => { toast.success("Message sent!"); setComposeOpen(false); }}>
                  <SendHorizonal className="h-4 w-4 mr-2" /> Send Message
                </Button>
                <Button variant="ghost" onClick={() => setComposeOpen(false)}>Cancel</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// DOCS TAB — Document vault
// ════════════════════════════════════════════════════════════════════════════

export function DocsTab({ studentId }: { studentId: string }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");

  const documents = [
    { name: "Term 2 Report Card", type: "Report", date: "Jun 15, 2025", status: "available", icon: Trophy },
    { name: "Term 3 Fee Invoice", type: "Finance", date: "Jul 1, 2025", status: "available", icon: Receipt },
    { name: "Admission Letter", type: "Admin", date: "Jan 5, 2025", status: "available", icon: FileText },
    { name: "Medical Consent Form", type: "Health", date: "Jan 10, 2025", status: "pending_signature", icon: Heart },
    { name: "Transport Agreement", type: "Transport", date: "Jan 5, 2025", status: "available", icon: Bus },
  ];

  const filteredDocs = documents.filter(d =>
    (filterType === "all" || d.type.toLowerCase() === filterType.toLowerCase()) &&
    (d.name.toLowerCase().includes(searchQuery.toLowerCase()) || d.type.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const pendingSignatures = documents.filter(d => d.status === "pending_signature").length;

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        <StatCard label="Documents" value={String(documents.length)} sub="In vault" icon={FolderOpen} accent={C.accent} compact />
        <StatCard label="Reports" value="3" sub="Available" icon={Trophy} accent={C.gold} compact />
        <StatCard label="Pending" value={String(pendingSignatures)} sub="Signatures" icon={FilePen} accent={pendingSignatures > 0 ? C.red : C.green} compact />
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: C.textMuted }} />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search documents..."
          className="pl-9 h-10 text-xs"
          style={{ background: C.card, borderColor: C.border }}
        />
      </div>

      {/* Filter pills */}
      <div className="flex gap-1.5 overflow-x-auto [scrollbar-width:none]">
        {["all", "report", "finance", "admin", "health"].map((type) => (
          <button
            key={type}
            onClick={() => setFilterType(type)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold capitalize whitespace-nowrap transition-all"
            style={{ background: filterType === type ? C.accent : C.bg, color: filterType === type ? "white" : C.textMuted }}
          >
            {type}
          </button>
        ))}
      </div>

      {/* Document list */}
      <div className="space-y-2">
        {filteredDocs.map((doc, i) => (
          <motion.div
            key={i}
            className="flex items-center gap-3 p-3 rounded-xl border transition-all hover:shadow-md"
            style={{ background: C.card, borderColor: C.border }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
          >
            <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: doc.status === "pending_signature" ? C.redLight : C.blueLight }}>
              <doc.icon className="h-5 w-5" style={{ color: doc.status === "pending_signature" ? C.red : C.accent }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold truncate" style={{ color: C.text }}>{doc.name}</p>
              <p className="text-[10px]" style={{ color: C.textMuted }}>{doc.type} · {doc.date}</p>
            </div>
            {doc.status === "pending_signature" ? (
              <Button size="sm" className="h-7 text-[10px]" style={{ background: C.red, color: "white" }}>
                Sign Now
              </Button>
            ) : (
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0">
                <Download className="h-3.5 w-3.5" style={{ color: C.textMuted }} />
              </Button>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TIMELINE TAB — Narrative layer
// ════════════════════════════════════════════════════════════════════════════

export function TimelineTab({ studentId }: { studentId: string }) {
  const [filter, setFilter] = useState("all");

  const events = [
    { date: "Today, 3:15 PM", title: "Assessment graded", desc: "Mathematics Quiz 4: 18/20", type: "academic", icon: Trophy, color: C.green },
    { date: "Today, 12:30 PM", title: "Lunch taken", desc: "School cafeteria", type: "school", icon: Utensils, color: C.orange },
    { date: "Today, 7:05 AM", title: "Arrived at school", desc: "Checked in at main gate", type: "attendance", icon: MapPin, color: C.blue },
    { date: "Yesterday, 4:45 PM", title: "Transport check-out", desc: "Boarded Bus UG 0042", type: "transport", icon: Bus, color: C.purple },
    { date: "Yesterday, 2:00 PM", title: "Fee payment received", desc: "UGX 500,000 via Mobile Money", type: "finance", icon: CreditCard, color: C.green },
    { date: "Jul 3, 2025", title: "Medical record updated", desc: "Annual check-up completed", type: "health", icon: Heart, color: C.pink },
    { date: "Jul 2, 2025", title: "Assignment graded", desc: "Physics Lab Report: 28/30", type: "academic", icon: ClipboardList, color: C.green },
    { date: "Jul 1, 2025", title: "Invoice generated", desc: "Term 3 Fee Invoice - UGX 3,500,000", type: "finance", icon: Receipt, color: C.orange },
  ];

  const filteredEvents = events.filter(e => filter === "all" || e.type === filter);

  const FILTERS = [
    { k: "all", label: "All" },
    { k: "academic", label: "Academic" },
    { k: "finance", label: "Finance" },
    { k: "health", label: "Health" },
    { k: "transport", label: "Transport" },
  ];

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex gap-1.5 overflow-x-auto [scrollbar-width:none]">
        {FILTERS.map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all"
            style={{ background: filter === f.k ? C.accent : C.bg, color: filter === f.k ? "white" : C.textMuted }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Timeline */}
      <div className="relative pl-6">
        <div className="absolute left-2 top-0 bottom-0 w-0.5 rounded-full" style={{ background: C.border }} />

        <div className="space-y-4">
          {filteredEvents.map((event, i) => (
            <motion.div
              key={i}
              className="relative"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <div className="absolute -left-4 top-1 h-4 w-4 rounded-full border-2 flex items-center justify-center" style={{ background: C.card, borderColor: event.color }}>
                <div className="h-2 w-2 rounded-full" style={{ background: event.color }} />
              </div>
              <div className="p-3 rounded-xl border" style={{ background: C.card, borderColor: C.border }}>
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${event.color}15` }}>
                    <event.icon className="h-4 w-4" style={{ color: event.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold" style={{ color: C.text }}>{event.title}</p>
                    <p className="text-[10px]" style={{ color: C.textMuted }}>{event.desc}</p>
                  </div>
                  <span className="text-[10px] shrink-0" style={{ color: C.textLight }}>{event.date}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MAIN STUDENT CARD COMPONENT
// ════════════════════════════════════════════════════════════════════════════

interface StudentCardProps {
  studentId: string;
  studentName?: string;
  studentNo?: string;
  schoolId?: string;
  schoolName?: string;
  classLabel?: string;
  grade?: string;
  photo?: string;
  busRoute?: string | null;
  boarding?: boolean;
  initialTab?: StudentTabType;
  onMessageTeacher?: () => void;
  onPayFees?: () => void;
}

export function StudentCardEnhanced({
  studentId,
  studentName = "Emma Nakamyabbbbb",
  studentNo = "STU-2024-0042",
  schoolName = "Greenhill Academy",
  classLabel = "Primary 6A",
  grade = "P6",
  photo,
  busRoute = "Route B - Kampala Road",
  boarding = false,
  initialTab = "overview",
  onMessageTeacher,
  onPayFees,
}: StudentCardProps) {
  const [activeTab, setActiveTab] = useState<StudentTabType>(initialTab);

  const TAB_CONFIG: { k: StudentTabType; label: string; icon: React.ComponentType<{ className?: string }>; badge?: number }[] = [
    { k: "overview", label: "Overview", icon: LayoutDashboard },
    { k: "academics", label: "Academics", icon: GraduationCap },
    { k: "attendance", label: "Attendance", icon: Calendar },
    { k: "finance", label: "Finance", icon: Wallet, badge: 1 },
    { k: "health", label: "Health", icon: Heart },
    { k: "transport", label: "Transport", icon: Bus },
    { k: "docs", label: "Docs", icon: FileText, badge: 1 },
    { k: "timeline", label: "Timeline", icon: Activity },
  ];

  return (
    <div className="rounded-2xl border overflow-hidden shadow-sm" style={{ background: C.card, borderColor: C.cardBorder }}>
      {/* Tab bar */}
      <div className="border-b overflow-x-auto [scrollbar-width:none]" style={{ borderColor: C.border }}>
        <div className="flex min-w-max">
          {TAB_CONFIG.map((tab) => {
            const isActive = activeTab === tab.k;
            return (
              <button
                key={tab.k}
                onClick={() => setActiveTab(tab.k)}
                className="relative flex items-center gap-1.5 px-3 py-3 text-xs font-medium transition-all"
                style={{ color: isActive ? C.accent : C.textMuted }}
              >
                <tab.icon className="h-3.5 w-3.5" />
                <span>{tab.label}</span>
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span className="h-4 min-w-[16px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center" style={{ background: C.redLight, color: C.red }}>
                    {tab.badge}
                  </span>
                )}
                {isActive && (
                  <motion.div
                    layoutId="activeTabIndicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5"
                    style={{ background: C.accent }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="p-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
          >
            {activeTab === "overview" && (
              <OverviewTab studentId={studentId} onMessageTeacher={onMessageTeacher} onPayFees={onPayFees} />
            )}
            {activeTab === "academics" && (
              <AcademicsTab
                studentId={studentId}
                subjects={DEMO_SUBJECTS}
                assessments={DEMO_ASSESSMENTS}
                assignments={DEMO_ASSIGNMENTS}
              />
            )}
            {activeTab === "attendance" && (
              <AttendanceTab studentId={studentId} />
            )}
            {activeTab === "finance" && (
              <FinanceTab studentId={studentId} onPayFees={onPayFees} />
            )}
            {activeTab === "health" && (
              <HealthTab studentId={studentId} />
            )}
            {activeTab === "transport" && (
              <TransportTab />
            )}
            {activeTab === "docs" && (
              <DocsTab studentId={studentId} />
            )}
            {activeTab === "timeline" && (
              <TimelineTab studentId={studentId} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default StudentCardEnhanced;
